import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFinanceSectorUser } from '../../../../utils/setorFinanceiro';
import { Card, CardBody, Container, Media, Spinner } from 'reactstrap';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import ArrowBack from '@material-ui/icons/ArrowBack';
import MaterialReactTable from 'material-react-table';
import moment from 'moment';
import SimpleCard from '../../../../utils/card/SimpleCard';
import currencyFormatter from '../../../../utils/currencyFormatter';
import { getDocs, selectValues } from '../../../../utils/fc';
import qs from 'qs';
import oikosApi from '../../../../services/oikos';
import { getFileName, uploadFileToDocumento } from '../../../../utils/oikosUploadDocumento';
import { OIKOS_FILES_BASE, TP_DOC_NF_DANFE, TP_DOC_NF_XML, TP_DOC_REC_LIBERACAO_FATURAMENTO } from '../../../../constants/oikosReceitas';
import { notifyError, notifySuccess } from '../../../../utils/notify';
import ModalOikosDocsViewer from '../../../../components/Modals/ModalOikosDocsViewer/ModalOikosDocsViewer';
import ModalDocsSuporteEnvio from '../../../../components/Modals/ModalDocsSuporteEnvio/ModalDocsSuporteEnvio';
import FatSupportDocsIcon from '../../../../components/Financial/FatSupportDocsIcon';
import { filterLibDocs } from '../../../../utils/recLiberacaoFaturamento';
import { getOikosApiUrl } from '../../../../config/backendEnv';
import {
  canEmitNfse,
  DEFAULT_COD_TRIB_OPTIONS,
  emitNfse,
  getEmitMissingFields,
  getKaminoEmitStatus,
  kaminoStatusLabel,
  lerNfseKamino,
  checkPendingNfse,
  enviarEmailNfseTeste,
} from '../../../../utils/kaminoNfse';
import PacotesKaminoMes from './PacotesKaminoMes';
import {
  canDesfazerFaturamentoReceita,
  desfazerFaturamentoReceita,
  isRecFaturada,
  listarNfsAtivasParaVinculo,
  vincularNfExistente,
} from '../../../../utils/desfazerFaturamentoReceita';
import { diffDays, fmtDateBr, inRange, isFaturado, isLiberado, monthRefToRange, num, parseDate, startOfDay, startOfToday } from './utils';
import { avaliarGuardrailNf, preencherTemplateNfDescricao, templateNfTemChaves } from './nfDescricaoTemplate';
import { matchProjFatTpl } from '../../../../utils/projFatTpl';
import SearchableSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';

const oikosFilesBase = () =>
  getOikosApiUrl().replace(/\/_oikos_api\.php.*$/i, '/').replace(/\/?$/, '/');

const hasUsefulId = (v) => v != null && String(v).trim() !== '' && Number(v) !== 0;

const onlyDigits = (s) => String(s || '').replace(/\D/g, '');

const raizCnpj = (s) => onlyDigits(s).slice(0, 8);

const normCodTrib = (s) => {
  const d = onlyDigits(s);
  return d || String(s || '').trim();
};

const apiRows = (res) => (Array.isArray(res?.data) ? res.data : []);

const apiFirst = (res) => apiRows(res)[0] || null;

const recJaEmitida = (r) => Number(r?.rec_fat) === 1 || Number(r?.nf_id) > 0 || Number(r?.kamino_id) > 0;

const pickDonorRec = (list, recId) => {
  const billed = (Array.isArray(list) ? list : [])
    .filter((r) => Number(r?.rec_id) !== Number(recId) && recJaEmitida(r))
    .sort((a, b) => Number(b?.rec_id) - Number(a?.rec_id));
  return billed[0] || null;
};

const mapPjOption = (p) => {
  if (!p?.pj_id) return null;
  const nome = p.pj_razaosocial || p.pj_nomefanta || `PJ ${p.pj_id}`;
  const kid = p.kamino_id;
  const hasKid = hasUsefulId(kid);
  return {
    value: p.pj_id,
    label: hasKid ? nome : `${nome} (sem ID Kamino)`,
    kamino_id: hasKid ? kid : null,
    mun_id: p.mun_id ?? null,
    cnpj: p.pj_cnpj || '',
    nome,
  };
};

const pickPjByCnpj = (pjs, cnpj) => {
  const d = onlyDigits(cnpj);
  if (d.length < 8) return null;
  const raiz = d.slice(0, 8);
  const matches = (Array.isArray(pjs) ? pjs : []).filter((p) => raizCnpj(p.pj_cnpj) === raiz);
  if (!matches.length) return null;
  const exactKam = matches.find((p) => onlyDigits(p.pj_cnpj) === d && hasUsefulId(p.kamino_id));
  if (exactKam) return exactKam;
  const anyKam = matches.find((p) => hasUsefulId(p.kamino_id));
  return anyKam || matches[0];
};

const modoCampo = (list, campo) => {
  const counts = new Map();
  (Array.isArray(list) ? list : []).forEach((r) => {
    const v = r?.[campo];
    if (v == null || String(v).trim() === '') return;
    const k = String(v);
    counts.set(k, (counts.get(k) || 0) + 1);
  });
  let best = null;
  let n = 0;
  counts.forEach((c, k) => {
    if (c > n) {
      best = k;
      n = c;
    }
  });
  return best;
};

const KaminoStatusPill = ({ status }) => {
  const label = kaminoStatusLabel(status);
  const bg =
    status === 'sincronizado' ? 'rgba(86,100,210,0.14)' : status === 'aguardando' ? 'rgba(241,180,76,0.22)' : 'rgba(108,117,125,0.14)';
  const color = status === 'sincronizado' ? '#5664d2' : status === 'aguardando' ? '#b46900' : '#6c757d';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 800,
        backgroundColor: bg,
        color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
};

const summarizeFaturamento = (recs) => {
  const out = {
    totalCount: 0,
    faturadosCount: 0,
    liberadosCount: 0,
    bloqueadosCount: 0,
    valorTotal: 0,
    valorFaturados: 0,
    valorLiberados: 0,
    valorBloqueados: 0,
  };

  out.totalCount = Array.isArray(recs) ? recs.length : 0;
  if (!Array.isArray(recs) || recs.length === 0) return out;

  for (const r of recs) {
    const v = num(r?.rec_vlrBru);
    const fat = isFaturado(r);
    const lib = isLiberado(r);

    out.valorTotal += v;

    if (fat) {
      out.faturadosCount += 1;
      out.valorFaturados += v;
    } else if (lib) {
      out.liberadosCount += 1;
      out.valorLiberados += v;
    } else {
      out.bloqueadosCount += 1;
      out.valorBloqueados += v;
    }
  }

  return out;
};

const StatLinesFaturamento = ({ recs }) => {
  const s = useMemo(() => summarizeFaturamento(recs), [recs]);

  return (
    <div className="d-flex flex-column" style={{ gap: 6 }}>
      <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.1 }}>{currencyFormatter.format(s.valorTotal)}</div>
      <div style={{ fontSize: 12, color: '#6c757d' }}>
        <span style={{ fontWeight: 700, color: '#4e515e' }}>{s.totalCount}</span> faturamentos
      </div>

      <div style={{ height: 6 }} />

      <div className="d-flex flex-column" style={{ gap: 2, fontSize: 12 }}>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <div style={{ width: 44, fontWeight: 800 }}>{s.faturadosCount}</div>
          <div style={{ flex: 1 }}>faturados</div>
          <div style={{ fontWeight: 800 }}>{currencyFormatter.format(s.valorFaturados)}</div>
        </div>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <div style={{ width: 44, fontWeight: 800 }}>{s.liberadosCount}</div>
          <div style={{ flex: 1 }}>liberados</div>
          <div style={{ fontWeight: 800 }}>{currencyFormatter.format(s.valorLiberados)}</div>
        </div>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <div style={{ width: 44, fontWeight: 800 }}>{s.bloqueadosCount}</div>
          <div style={{ flex: 1 }}>bloqueados</div>
          <div style={{ fontWeight: 800 }}>{currencyFormatter.format(s.valorBloqueados)}</div>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ title, recs, onClick, className, selected }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={className}
      style={{ width: '100%' }}
      role="button"
      tabIndex={0}
      aria-pressed={!!selected}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (typeof onClick === 'function') onClick();
        }
      }}
    >
      <Card
        style={{
          transition: 'border 0.18s ease, box-shadow 0.18s ease',
          border: selected ? '2px solid #4e515e' : hovered ? '2px solid #505A62' : '2px solid transparent',
          boxShadow: selected ? '0 6px 20px rgba(0,0,0,0.10)' : undefined,
          cursor: 'pointer',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <CardBody>
          <Media>
            <Media body className="overflow-hidden">
              <div className="d-flex justify-content-between align-items-center">
                <div style={{ fontWeight: 'bold' }} className="text-truncate font-size-14 mb-2">
                  {title}
                </div>
              </div>
              <StatLinesFaturamento recs={recs} />
            </Media>
          </Media>
        </CardBody>
      </Card>
    </div>
  );
};

const YesNoDot = ({ value, trueColor = '#1cbb8c', falseColor = '#ff5757' }) => {
  const backgroundColor = value ? trueColor : falseColor;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ backgroundColor, borderRadius: '50%', width: '12px', height: '12px' }} />
    </div>
  );
};

const getSituacao = (rec, today) => {
  if (isFaturado(rec)) return 'faturado';
  const dtPrev = parseDate(rec?.rec_dtNfPrev);
  if (dtPrev && dtPrev < startOfDay(today)) return 'atrasado';
  if (isLiberado(rec)) return 'liberado';
  return 'bloqueado';
};

const situacaoLabel = (key) => {
  if (key === 'faturado') return 'Faturado';
  if (key === 'atrasado') return 'Atrasado';
  if (key === 'liberado') return 'Liberado';
  if (key === 'bloqueado') return 'Bloqueado';
  return key || '-';
};

const SituacaoPill = ({ value }) => {
  const bg =
    value === 'faturado'
      ? '#5664d2'
      : value === 'atrasado'
        ? '#ff5757'
        : value === 'liberado'
          ? '#1cbb8c'
          : '#4e515e';
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, color: '#fff', backgroundColor: bg, whiteSpace: 'nowrap' }}>
        {situacaoLabel(value)}
      </span>
    </div>
  );
};

const OIKOS_NF_UP_ENDPOINT = 'https://oikos.ecoss.is/oikos/_fin.nf.up.php';

const extractNfNumFromXmlText = (text) => {
  if (!text) return null;
  const m1 = text.match(/<Numero>\s*([^<]+)\s*<\/Numero>/i);
  if (m1?.[1]) return String(m1[1]).trim();
  const m2 = text.match(/<nNFSe>\s*([^<]+)\s*<\/nNFSe>/i);
  if (m2?.[1]) return String(m2[1]).trim();
  return null;
};

const extractFirstTagText = (xml, tag) => {
  if (!xml || !tag) return null;
  const re = new RegExp(`<${tag}>([\\s\\S]*?)<\\/${tag}>`, 'i');
  const m = String(xml).match(re);
  if (!m?.[1]) return null;
  return String(m[1]).trim();
};

const guessDateFromXml = (xml) => {
  const candidates = ['Competencia', 'competencia', 'DataEmissao', 'dataEmissao', 'dhEmi', 'dCompet', 'dEmi'];
  for (const t of candidates) {
    const v = extractFirstTagText(xml, t);
    if (!v) continue;
    const m = v.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m?.[1]) return m[1];
    const m2 = v.match(/^(\d{2}\/\d{2}\/\d{4})/);
    if (m2?.[1]) return m2[1];
  }
  return null;
};

const toCompetencia = (dateLike) => {
  const d = parseDate(dateLike);
  if (!d) return null;
  return moment(d).startOf('month').format('YYYY-MM-DD');
};

const ensureNfFromXml = async ({ xmlText, nfNum }) => {
  try {
    const existingRes = await oikosApi.get(
      `/?${qs.stringify({ action: 'get', table: 'nf', data: { nf_num: nfNum, nf_ativo: 1 } })}`
    );
    const existing = Array.isArray(existingRes?.data) ? existingRes.data[0] : null;
    if (existing?.nf_id) {
      // Garante que o conteúdo do XML fica salvo na coluna nf.nf_xml.
      await oikosApi.post('/', {
        action: 'update',
        table: 'nf',
        data: { nf_id: existing.nf_id, nf_xml: xmlText },
      });
      return { nf_id: existing.nf_id, nf_num: existing.nf_num, reused: true };
    }
  } catch (e) {}

  const dtGuess = guessDateFromXml(xmlText);
  const nf_comp = toCompetencia(dtGuess);
  const desc =
    extractFirstTagText(xmlText, 'Discriminacao') ||
    extractFirstTagText(xmlText, 'discriminacao') ||
    extractFirstTagText(xmlText, 'Descricao') ||
    extractFirstTagText(xmlText, 'descricao') ||
    null;

  const created = await oikosApi.post('/', {
    action: 'insert',
    table: 'nf',
    data: { nf_num: nfNum, nf_comp, nf_desc: desc, nf_status: 'importada', nf_ativo: 1, nf_xml: xmlText },
  });
  const row = Array.isArray(created?.data) ? created.data[0] : null;
  const nf_id = row?.nf_id;
  if (!nf_id) throw new Error('Falha ao criar NF (nf_id não retornado).');
  return { nf_id, nf_num: nfNum, reused: false, nf_comp };
};



const recsDoMesmoGrupoNf = (list, rec) => {
  const nf = Number(rec?.nf_id) || 0;
  const kid = Number(rec?.kamino_id) || 0;
  const me = Number(rec?.rec_id);
  const rows = Array.isArray(list) ? list : [];
  if (nf > 0) return rows.filter((r) => Number(r?.nf_id) === nf);
  if (kid > 0) return rows.filter((r) => Number(r?.kamino_id) === kid);
  return rows.filter((r) => Number(r?.rec_id) === me);
};

const recsComponiveisCom = (list, rec) => {
  const me = Number(rec?.rec_id);
  const orc = Number(rec?.orcRubr_id) || 0;
  const myKid = Number(rec?.kamino_id) || 0;
  const tom = Number(rec?.pj_tomador) || 0;
  if (orc <= 0) return [];
  return (Array.isArray(list) ? list : []).filter((r) => {
    if (Number(r?.rec_id) === me) return false;
    if (orc > 0 && Number(r?.orcRubr_id) !== orc) return false;
    if (!isLiberado(r) || isFaturado(r)) return false;
    const kid = Number(r?.kamino_id) || 0;
    if (kid > 0 && myKid > 0 && kid !== myKid) return false;
    if (kid > 0 && myKid === 0) return false;
    const rTom = Number(r?.pj_tomador) || 0;
    if (tom > 0 && rTom > 0 && rTom !== tom) return false;
    const myTpl = Number(rec?.projFatTpl_id) || 0;
    const rTpl = Number(r?.projFatTpl_id) || 0;
    if (myTpl > 0 && rTpl !== myTpl) return false;
    return true;
  });
};

const EmailTestButton = ({ rec, label }) => {
  const [open, setOpen] = useState(false);
  const recId = Number(rec?.rec_id) || 0;
  const hasNf = Number(rec?.nf_id) > 0;
  if (!hasNf || recId <= 0) {
    return <span style={{ color: '#9aa3ad', fontSize: 11 }}>?</span>;
  }
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        {label || 'Enviar e-mail'}
      </Button>
      <ModalDocsSuporteEnvio
        open={open}
        rec={rec}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const DetailsCard = ({ rec, recs, onRecPatched, onNeedReload }) => {
  const { user } = useSelector((state) => state.auth);
  const podeVerNf = useFinanceSectorUser();
  const canEditEmit = canEmitNfse(user);
  const canUndoFat = canDesfazerFaturamentoReceita({
    userCargo: user?.pf_cargo,
    permId: user?.perm_id,
  });

  const [nfModalOpen, setNfModalOpen] = useState(false);
  const [nfLoading, setNfLoading] = useState(false);
  const [nfError, setNfError] = useState(null);
  const [nf, setNf] = useState(null);

  const [nfId, setNfId] = useState(rec?.nf_id ?? null);
  const [recFatLocal, setRecFatLocal] = useState(rec?.rec_fat ?? null);
  const [kaminoIdLocal, setKaminoIdLocal] = useState(rec?.kamino_id ?? null);
  const pulledIssuedRef = useRef(null);

  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState(null);
  const [docs, setDocs] = useState([]);

  const [uploadingXml, setUploadingXml] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const [nfDescricao, setNfDescricao] = useState(rec?.rec_NFDescricao || '');
  const [kaminoImposto, setKaminoImposto] = useState(rec?.kamino_imposto != null && rec?.kamino_imposto !== '' ? String(rec.kamino_imposto) : '');
  const [codTrib, setCodTrib] = useState(rec?.contrato_codTrib || '');
  const [pjTomadorId, setPjTomadorId] = useState(rec?.pj_tomador ?? null);
  const [pjTomadorNomeLocal, setPjTomadorNomeLocal] = useState(rec?.pjTomadorNome || '');
  const [pjTomadorKaminoLocal, setPjTomadorKaminoLocal] = useState(rec?.pjTomadorKaminoId ?? null);
  const [munId, setMunId] = useState(rec?.mun_id ?? null);
  const [retTemplateId, setRetTemplateId] = useState(rec?.retTemplate_id ?? null);
  const [pjPrestadorId, setPjPrestadorId] = useState(rec?.pj_prestador ?? null);
  const [pjPrestadorNomeLocal, setPjPrestadorNomeLocal] = useState(rec?.pjPrestadorNome || '');
  const [pjPrestadorKaminoLocal, setPjPrestadorKaminoLocal] = useState(rec?.pjPrestadorKaminoId ?? null);
  const [cnoProjeto, setCnoProjeto] = useState('');
  const [fatProcOrientacao, setFatProcOrientacao] = useState('');
  const [nfDescTpl, setNfDescTpl] = useState('');
  const [projFatTplId, setProjFatTplId] = useState(rec?.projFatTpl_id ?? null);
  const [projFatTplNome, setProjFatTplNome] = useState('');
  const [fatTpls, setFatTpls] = useState([]);
  const [sugestaoOrigem, setSugestaoOrigem] = useState([]);
  const [savingNfFields, setSavingNfFields] = useState(false);
  const [emitting, setEmitting] = useState(false);
  const [syncingNfse, setSyncingNfse] = useState(false);
  const [composeRecIds, setComposeRecIds] = useState([]);
  const [attachRecIds, setAttachRecIds] = useState([]);
  const [attachingRecs, setAttachingRecs] = useState(false);

  useEffect(() => {
    pulledIssuedRef.current = null;
    setComposeRecIds([]);
    setAttachRecIds([]);
  }, [rec?.rec_id]);

  const [impostoOptions, setImpostoOptions] = useState([]);
  const [pjOptions, setPjOptions] = useState([]);
  const [munOptions, setMunOptions] = useState([]);
  const [codTribOptions, setCodTribOptions] = useState(DEFAULT_COD_TRIB_OPTIONS);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [libDocs, setLibDocs] = useState([]);
  const [evidenciaOpen, setEvidenciaOpen] = useState(false);

  const [undoOpen, setUndoOpen] = useState(false);
  const [undoLoading, setUndoLoading] = useState(false);
  const [linkNfOptions, setLinkNfOptions] = useState([]);
  const [linkNfLoading, setLinkNfLoading] = useState(false);
  const [linkNfSelected, setLinkNfSelected] = useState(null);
  const [linkNfSaving, setLinkNfSaving] = useState(false);

  const closeNfModal = () => setNfModalOpen(false);
  const openNfModal = () => setNfModalOpen(true);

  const recNfId = rec?.nf_id ?? null;
  const recFat = rec?.rec_fat ?? null;
  const recKaminoId = rec?.kamino_id ?? null;

  useEffect(() => {
    let cancelled = false;
    const loadLibDocs = async () => {
      if (!rec?.rec_id) {
        setLibDocs([]);
        return;
      }
      try {
        const d = await getDocs('rec', rec.rec_id);
        if (cancelled) return;
        setLibDocs(filterLibDocs(d));
      } catch (e) {
        if (cancelled) return;
        setLibDocs([]);
      }
    };
    loadLibDocs();
    return () => {
      cancelled = true;
    };
  }, [rec?.rec_id]);

  useEffect(() => {
    let cancelled = false;
    const fetchNf = async () => {
      if (!podeVerNf || !recNfId) {
        setNf(null);
        setNfError(null);
        setNfLoading(false);
        return;
      }
      setNfLoading(true);
      setNfError(null);
      try {
        const res = await oikosApi.get(`/?${qs.stringify({ action: 'get', table: 'nf', data: { nf_id: recNfId, nf_ativo: 1 } })}`);
        if (cancelled) return;
        const row = Array.isArray(res?.data) ? res.data[0] : null;
        if (!row) {
          setNf(null);
          setNfError(`NF não encontrada para nf_id=${recNfId}.`);
          return;
        }
        setNf(row);
      } catch (e) {
        if (cancelled) return;
        setNf(null);
        setNfError(e?.message || 'Erro ao buscar a Nota Fiscal.');
      } finally {
        if (!cancelled) setNfLoading(false);
      }
    };
    fetchNf();
    return () => {
      cancelled = true;
    };
  }, [recNfId, podeVerNf]);

  useEffect(() => {
    setNfId(recNfId);
    setRecFatLocal(recFat);
    setKaminoIdLocal(recKaminoId);
    setNfDescricao(rec?.rec_NFDescricao || '');
    setKaminoImposto(rec?.kamino_imposto != null && rec?.kamino_imposto !== '' ? String(rec.kamino_imposto) : '');
    setCodTrib(rec?.contrato_codTrib || '');
    setPjTomadorId(rec?.pj_tomador ?? null);
    setPjTomadorNomeLocal(rec?.pjTomadorNome || '');
    setPjTomadorKaminoLocal(rec?.pjTomadorKaminoId ?? null);
    setMunId(rec?.mun_id ?? null);
    setRetTemplateId(rec?.retTemplate_id != null && rec?.retTemplate_id !== '' ? Number(rec.retTemplate_id) : null);
    setPjPrestadorId(rec?.pj_prestador ?? null);
    setPjPrestadorNomeLocal(rec?.pjPrestadorNome || '');
    setPjPrestadorKaminoLocal(rec?.pjPrestadorKaminoId ?? null);
    setCnoProjeto('');
    setFatProcOrientacao('');
    setNfDescTpl('');
    setProjFatTplId(rec?.projFatTpl_id ?? null);
    setProjFatTplNome('');
    setFatTpls([]);
    setSugestaoOrigem([]);
  }, [rec?.rec_id]);

  useEffect(() => {
    if (!templateNfTemChaves(nfDescricao)) return undefined;
    const next = preencherTemplateNfDescricao(nfDescricao, rec, []);
    if (next && next !== nfDescricao) setNfDescricao(next);
    return undefined;
  }, [rec?.rec_id, rec?.rec_desc, nfDescricao]);

  useEffect(() => {
    if (!canEditEmit) return undefined;
    let cancelled = false;
    const loadOptions = async () => {
      setOptionsLoading(true);
      try {
        const [muns, pjsRes] = await Promise.all([
          selectValues('mun', 'mun_id', 'mun_nome', { mun_ativo: 1 }).catch(() => []),
          oikosApi
            .get(
              `/?${qs.stringify({
                action: 'get',
                table: 'pj',
                data: { pj_ativo: 1 },
              })}`
            )
            .catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;

        setMunOptions(Array.isArray(muns) ? muns : []);

        const pjRows = Array.isArray(pjsRes?.data) ? pjsRes.data : [];
        const mapped = pjRows
          .map((p) => mapPjOption(p))
          .filter(Boolean)
          .sort((a, b) => String(a.label).localeCompare(String(b.label), 'pt-BR'));
        setPjOptions(mapped);

        const tribSet = new Map(DEFAULT_COD_TRIB_OPTIONS.map((o) => [String(o.value), o]));
        if (rec?.contrato_codTrib) {
          const v = String(rec.contrato_codTrib);
          if (!tribSet.has(v)) tribSet.set(v, { value: v, label: v });
        }
        setCodTribOptions(Array.from(tribSet.values()));
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    };
    loadOptions();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEditEmit]);

  useEffect(() => {
    const recId = rec?.rec_id;
    if (!recId || !canEditEmit) return undefined;
    let cancelled = false;
    const loadSugestao = async () => {
      try {
        const fetchById = async (table, key, id) => {
          if (!hasUsefulId(id)) return null;
          const res = await oikosApi.get(`/?${qs.stringify({ action: 'get', table, data: { [key]: Number(id) } })}`);
          return apiFirst(res);
        };
        const munFromIbge = async (ibge) => {
          const code = onlyDigits(ibge);
          if (!code) return null;
          const res = await oikosApi.get(`/?${qs.stringify({ action: 'get', table: 'mun', data: { mun_IBGECod: Number(code) } })}`);
          const row = apiFirst(res);
          return hasUsefulId(row?.mun_id) ? Number(row.mun_id) : null;
        };
        const mergePj = (pj) => {
          const opt = mapPjOption(pj);
          if (!opt) return;
          setPjOptions((prev) => {
            if (prev.some((o) => Number(o.value) === Number(opt.value))) return prev;
            return [...prev, opt].sort((a, b) => String(a.label).localeCompare(String(b.label), 'pt-BR'));
          });
        };

        const projRes = rec?.orcRubr_id
          ? await oikosApi.get(`/?${qs.stringify({
            action: 'get',
            table: 'proj',
            data: { orcRubr_id: rec.orcRubr_id, proj_ativo: 1 },
          })}`)
          : { data: [] };
        if (cancelled) return;
        const proj = apiFirst(projRes);
        const contratoRes = proj?.contrato_id
          ? await oikosApi.get(`/?${qs.stringify({
            action: 'get',
            table: 'contrato',
            data: { contrato_id: proj.contrato_id },
          })}`)
          : { data: [] };
        if (cancelled) return;
        const contrato = apiFirst(contratoRes);
        const tplRes = await oikosApi.get(`/?${qs.stringify({ action: 'get', table: 'retTemplate' })}`);
        if (cancelled) return;
        const templates = apiRows(tplRes);

        let historico = [];
        if (rec?.orcRubr_id) {
          const histRes = await oikosApi.get(`/?${qs.stringify({
            action: 'get',
            table: 'rec',
            data: { orcRubr_id: rec.orcRubr_id, rec_ativo: 1 },
          })}`);
          if (cancelled) return;
          historico = apiRows(histRes);
        }
        const donor = pickDonorRec(historico, recId);
        let nfAnt = null;
        if (hasUsefulId(donor?.nf_id)) {
          nfAnt = await fetchById('nf', 'nf_id', donor.nf_id);
          if (cancelled) return;
        }

        const origens = [];
        let fatTpls = [];
        if (proj?.proj_id) {
          const fatTplRes = await oikosApi.get(`/?${qs.stringify({
            action: 'get',
            table: 'projFatTpl',
            data: { proj_id: proj.proj_id, projFatTpl_ativo: 1 },
          })}`);
          if (cancelled) return;
          fatTpls = apiRows(fatTplRes);
        }
        setFatTpls(fatTpls);
        const serie = matchProjFatTpl(rec, fatTpls);
        if (serie?.projFatTpl_id) {
          setProjFatTplId(Number(serie.projFatTpl_id));
          setProjFatTplNome(String(serie.projFatTpl_nome || ''));
          origens.push(`s?rie ${serie.projFatTpl_nome}`);
        } else {
          setProjFatTplId(rec?.projFatTpl_id ?? null);
          setProjFatTplNome('');
        }
        const descRec = String(rec?.rec_NFDescricao || '').trim();
        const descProj = String(serie?.projFatTpl_nfDescTpl || proj?.proj_nfDescTpl || '').trim();
        const descBase = descRec || descProj;
        if (descBase && (!descRec || templateNfTemChaves(descRec))) {
          const preenchida = preencherTemplateNfDescricao(descBase, rec, historico);
          setNfDescricao(preenchida);
          if (!descRec && descProj) origens.push('descri??o do projeto');
          if (templateNfTemChaves(descBase) && preenchida !== descBase) {
            origens.push(String(rec?.rec_desc || '').trim()
              ? 'chaves { } preenchidas a partir da descri??o da receita'
              : 'chaves { } preenchidas com parcela e valor bruto (compet?ncia e PO s? v?m da descri??o da receita)');
          }
        }

        const aplicarCodTrib = (raw, origem) => {
          const v = normCodTrib(raw);
          if (!v) return false;
          setCodTrib(v);
          setCodTribOptions((prev) => (
            prev.some((o) => String(o.value) === v) ? prev : [...prev, { value: v, label: v }]
          ));
          origens.push(origem);
          return true;
        };
        if (!normCodTrib(rec?.contrato_codTrib)) {
          if (hasUsefulId(donor?.contrato_codTrib) || String(donor?.contrato_codTrib || '').trim()) {
            aplicarCodTrib(donor.contrato_codTrib, 'c?digo de servi?o das notas anteriores');
          } else if (nfAnt?.nf_itemListaServico) {
            aplicarCodTrib(nfAnt.nf_itemListaServico, 'c?digo de servi?o das NFS-e anteriores');
          } else if (contrato?.contrato_codTrib) {
            aplicarCodTrib(contrato.contrato_codTrib, 'c?digo tribut?rio do contrato');
          }
        } else if (String(rec.contrato_codTrib).includes('.')) {
          aplicarCodTrib(rec.contrato_codTrib, 'c?digo de servi?o (pontos removidos)');
        }

        if (!hasUsefulId(rec?.pj_prestador) && hasUsefulId(donor?.pj_prestador)) {
          setPjPrestadorId(Number(donor.pj_prestador));
          origens.push('prestador das notas anteriores');
        } else if (!hasUsefulId(rec?.pj_prestador) && hasUsefulId(contrato?.contrato_contratada)) {
          setPjPrestadorId(Number(contrato.contrato_contratada));
          origens.push('prestador do contrato');
        }

        let tomadorPj = hasUsefulId(rec?.pj_tomador) ? await fetchById('pj', 'pj_id', rec.pj_tomador) : null;
        if (cancelled) return;
        if (!hasUsefulId(tomadorPj?.kamino_id) && hasUsefulId(donor?.pj_tomador)) {
          const dPj = await fetchById('pj', 'pj_id', donor.pj_tomador);
          if (cancelled) return;
          if (hasUsefulId(dPj?.kamino_id)) {
            tomadorPj = dPj;
            origens.push('tomador das notas anteriores deste projeto');
          }
        }
        if (!hasUsefulId(tomadorPj?.kamino_id) && nfAnt?.nf_tomCNPJ) {
          const pjsRes = await oikosApi.get(`/?${qs.stringify({ action: 'get', table: 'pj', data: { pj_ativo: 1 } })}`);
          if (cancelled) return;
          const byNf = pickPjByCnpj(apiRows(pjsRes), nfAnt.nf_tomCNPJ);
          if (hasUsefulId(byNf?.kamino_id)) {
            tomadorPj = byNf;
            origens.push('tomador das NFS-e anteriores (mesmo CNPJ na Kamino)');
          }
        }
        if (!hasUsefulId(tomadorPj?.kamino_id) && hasUsefulId(contrato?.contrato_contratante)) {
          const contratante = await fetchById('pj', 'pj_id', contrato.contrato_contratante);
          if (cancelled) return;
          if (hasUsefulId(contratante?.kamino_id)) {
            tomadorPj = contratante;
            origens.push('tomador do contrato');
          } else if (contratante?.pj_cnpj) {
            const pjsRes = await oikosApi.get(`/?${qs.stringify({ action: 'get', table: 'pj', data: { pj_ativo: 1 } })}`);
            if (cancelled) return;
            const byCnpj = pickPjByCnpj(apiRows(pjsRes), contratante.pj_cnpj);
            if (hasUsefulId(byCnpj?.kamino_id)) {
              tomadorPj = byCnpj;
              origens.push('cliente Kamino do mesmo CNPJ do contratante');
            } else if (!tomadorPj && contratante) {
              tomadorPj = contratante;
              origens.push('contratante do contrato (sem ID Kamino)');
            }
          }
        }
        if (tomadorPj?.pj_id) {
          mergePj(tomadorPj);
          setPjTomadorId(Number(tomadorPj.pj_id));
          setPjTomadorNomeLocal(tomadorPj.pj_razaosocial || tomadorPj.pj_nomefanta || '');
          setPjTomadorKaminoLocal(hasUsefulId(tomadorPj.kamino_id) ? tomadorPj.kamino_id : null);
        }

        if (!hasUsefulId(rec?.mun_id)) {
          if (hasUsefulId(donor?.mun_id)) {
            setMunId(Number(donor.mun_id));
            origens.push('munic?pio de presta??o das notas anteriores');
          } else {
            const munTomador = hasUsefulId(tomadorPj?.mun_id) ? Number(tomadorPj.mun_id) : null;
            const munIbgeTom = nfAnt?.nf_tomEndCodMun ? await munFromIbge(nfAnt.nf_tomEndCodMun) : null;
            if (cancelled) return;
            if (munTomador) {
              setMunId(munTomador);
              origens.push('munic?pio de presta??o (cadastro do tomador)');
            } else if (munIbgeTom) {
              setMunId(munIbgeTom);
              origens.push('munic?pio de presta??o (NFS-e anteriores)');
            }
          }
        }

        let tplId = hasUsefulId(rec?.retTemplate_id) ? Number(rec.retTemplate_id) : null;
        let origemImposto = '';
        if (!tplId && hasUsefulId(proj?.retTemplate_id)) {
          tplId = Number(proj.retTemplate_id);
          origemImposto = 'template de reten??o do projeto';
        }
        if (!tplId && hasUsefulId(rec?.kamino_imposto)) {
          const byKamino = templates.find((t) => String(t.kamino_id) === String(rec.kamino_imposto));
          if (byKamino) tplId = Number(byKamino.retTemplate_id);
        }
        if (!tplId && hasUsefulId(donor?.retTemplate_id)) {
          tplId = Number(donor.retTemplate_id);
          origemImposto = 'tabela de imposto das notas anteriores deste projeto';
        }
        if (!tplId && hasUsefulId(donor?.kamino_imposto)) {
          const byDonor = templates.find((t) => String(t.kamino_id) === String(donor.kamino_imposto));
          if (byDonor) {
            tplId = Number(byDonor.retTemplate_id);
            origemImposto = 'tabela de imposto das notas anteriores deste projeto';
          }
        }
        if (!tplId) {
          const tribAlvo = normCodTrib(rec?.contrato_codTrib) || normCodTrib(donor?.contrato_codTrib) || normCodTrib(contrato?.contrato_codTrib) || normCodTrib(nfAnt?.nf_itemListaServico);
          const billed = (Array.isArray(recs) ? recs : []).filter((r) => recJaEmitida(r) && hasUsefulId(r.kamino_imposto));
          const sameTrib = tribAlvo
            ? billed.filter((r) => normCodTrib(r.contrato_codTrib) === tribAlvo)
            : billed;
          const modo = modoCampo(sameTrib.length ? sameTrib : billed, 'kamino_imposto');
          if (modo) {
            const byModo = templates.find((t) => String(t.kamino_id) === String(modo));
            if (byModo) {
              tplId = Number(byModo.retTemplate_id);
              origemImposto = sameTrib.length
                ? `tabela de imposto mais usada no c?digo ${tribAlvo}`
                : 'tabela de imposto mais usada nas notas emitidas';
            }
          }
        }
        const tpl = templates.find((t) => Number(t.retTemplate_id) === Number(tplId));
        if (tplId) setRetTemplateId(tplId);
        if (tpl?.kamino_id != null && String(tpl.kamino_id).trim() !== '') {
          if (!hasUsefulId(rec?.kamino_imposto) || origemImposto) {
            setKaminoImposto(String(tpl.kamino_id));
          } else if (hasUsefulId(rec?.retTemplate_id)) {
            setKaminoImposto(String(tpl.kamino_id));
          }
        }
        if (origemImposto) origens.push(origemImposto);
        setImpostoOptions(
          templates
            .filter((t) => Number(t.retTemplate_ativo) === 1 || Number(t.retTemplate_id) === Number(tplId))
            .map((t) => ({
              value: String(t.kamino_id),
              label: t.retTemplate_desc || `Template ${t.retTemplate_id}`,
              retTemplate_id: Number(t.retTemplate_id),
            }))
        );

        if (serie) {
          if (!hasUsefulId(rec?.mun_id) && hasUsefulId(serie.mun_id)) {
            setMunId(Number(serie.mun_id));
            origens.push('municipio da serie');
          }
          if (!hasUsefulId(rec?.pj_prestador) && hasUsefulId(serie.pj_prestador)) {
            setPjPrestadorId(Number(serie.pj_prestador));
            origens.push('prestador da serie');
          }
          if (!hasUsefulId(rec?.retTemplate_id) && hasUsefulId(serie.retTemplate_id)) {
            setRetTemplateId(Number(serie.retTemplate_id));
            origens.push('retencao da serie');
          }
          if (!normCodTrib(rec?.contrato_codTrib) && serie.contrato_codTrib) {
            aplicarCodTrib(serie.contrato_codTrib, 'codigo tributario da serie');
          }
        }
        setCnoProjeto(String(proj?.proj_cno || '').trim());
        setFatProcOrientacao(String(proj?.proj_fatProc || '').trim());
        setNfDescTpl(descProj);
        setSugestaoOrigem(origens);
      } catch (e) {
        console.warn('[DetailsCard] sugest?o da NF:', e?.message || e);
      }
    };
    loadSugestao();
    return () => {
      cancelled = true;
    };
  }, [rec?.rec_id, canEditEmit]);

  const lastTomadorIdRef = useRef(pjTomadorId);
  useEffect(() => {
    if (!pjTomadorId || !pjOptions.length) return;
    const opt = pjOptions.find((o) => Number(o.value) === Number(pjTomadorId));
    if (!opt) return;
    setPjTomadorNomeLocal(opt.nome || '');
    const idChanged = Number(lastTomadorIdRef.current) !== Number(pjTomadorId);
    lastTomadorIdRef.current = pjTomadorId;
    if (idChanged) {
      setPjTomadorKaminoLocal(opt.kamino_id ?? null);
    } else if (pjTomadorKaminoLocal == null && opt.kamino_id != null && opt.kamino_id !== '') {
      setPjTomadorKaminoLocal(opt.kamino_id);
    }
  }, [pjTomadorId, pjOptions]);

  useEffect(() => {
    if (!pjPrestadorId || !pjOptions.length) return;
    const opt = pjOptions.find((o) => Number(o.value) === Number(pjPrestadorId));
    if (!opt) return;
    setPjPrestadorNomeLocal(opt.nome || '');
    setPjPrestadorKaminoLocal(opt.kamino_id ?? null);
  }, [pjPrestadorId, pjOptions]);

  const refreshDocs = async (id) => {
    if (!podeVerNf || !id) {
      setDocs([]);
      setDocsError(null);
      return;
    }
    setDocsLoading(true);
    setDocsError(null);
    try {
      const res = await getDocs('nf', id);
      setDocs(Array.isArray(res) ? res : []);
    } catch (e) {
      setDocs([]);
      setDocsError(e?.message || 'Erro ao buscar anexos da NF.');
    } finally {
      setDocsLoading(false);
    }
  };

  useEffect(() => {
    if (!podeVerNf) {
      setDocs([]);
      setDocsError(null);
      return;
    }
    refreshDocs(nfId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nfId, podeVerNf]);

  const aplicarImportacaoKamino = (hit, kaminoIdHint) => {
    if (!hit?.nf_id) return;
    setNfId(hit.nf_id);
    setRecFatLocal(1);
    refreshDocs(hit.nf_id);
    if (typeof onRecPatched === 'function') {
      onRecPatched({
        nf_id: hit.nf_id,
        rec_fat: 1,
        kamino_id: Number(kaminoIdHint || rec?.kamino_id || kaminoIdLocal) || undefined,
      });
    }
  };

  const sincronizarNfseDestaReceita = async ({ silent } = {}) => {
    if (!rec?.rec_id) return false;
    setSyncingNfse(true);
    try {
      const res = await checkPendingNfse({ rec_id: rec.rec_id, limit: 1 });
      const hit = (res?.imported || []).find((item) => Number(item?.rec_id) === Number(rec.rec_id));
      if (hit?.nf_id) {
        aplicarImportacaoKamino(hit);
        if (!silent) {
          notifySuccess(`NFS-e ${hit.nf_num || ''} sincronizada. XML e PDF gravados.`);
        }
        return hit;
      }
      if (!silent && Array.isArray(res?.errors) && res.errors.length) {
        notifyError(res.errors[0]);
      } else if (!silent) {
        notifyError('A Kamino ainda não confirmou a emissão desta NFS-e, ou o XML/PDF não está disponível.');
      }
      return false;
    } catch (e) {
      if (!silent) notifyError(e?.message || 'Falha ao sincronizar a NFS-e.');
      return false;
    } finally {
      setSyncingNfse(false);
    }
  };

  useEffect(() => {
    const kid = Number(rec?.kamino_id) || 0;
    if (!rec?.rec_id || kid <= 0) return undefined;
    if (Number(rec?.nf_id) > 0 || Number(rec?.rec_fat) === 1) return undefined;
    let cancelled = false;
    checkPendingNfse({ rec_id: rec.rec_id, limit: 1 })
      .then((res) => {
        if (cancelled) return;
        const hit = (res?.imported || []).find((item) => Number(item?.rec_id) === Number(rec.rec_id));
        if (!hit?.nf_id) return;
        aplicarImportacaoKamino(hit, kid);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec?.rec_id, rec?.kamino_id]);

  const recDraftForFatHook = {
    ...rec,
    nf_id: nfId ?? rec?.nf_id,
    rec_fat: recFatLocal ?? rec?.rec_fat,
  };
  const canShowLinkNf = podeVerNf && canUndoFat && !!rec?.rec_id && !isRecFaturada(recDraftForFatHook);

  useEffect(() => {
    if (!canShowLinkNf) {
      setLinkNfOptions([]);
      setLinkNfSelected(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setLinkNfLoading(true);
      try {
        const opts = await listarNfsAtivasParaVinculo();
        if (cancelled) return;
        setLinkNfOptions(opts);
      } catch (e) {
        if (cancelled) return;
        setLinkNfOptions([]);
        notifyError(e?.message || 'Falha ao listar NFs para vínculo.');
      } finally {
        if (!cancelled) setLinkNfLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [canShowLinkNf, rec?.rec_id]);

  const LabelValue = ({ label, value, grow }) => (
    <div style={{ display: 'flex', gap: 6, flex: grow ? 1 : undefined, minWidth: 0 }}>
      <span style={{ color: '#6c757d', whiteSpace: 'nowrap' }}>{label}:</span>
      <span style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value ?? '-'}</span>
    </div>
  );

  const formatDateTime = (v) => (v ? moment(v).format('DD/MM/YYYY HH:mm') : '-');
  const formatDateOnly = (v) => (v ? fmtDateBr(v) : '-');
  const formatMoney = (v) => currencyFormatter.format(num(v));
  const formatPercent = (v) => `${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num(v))}%`;

  const idsSelecionadosNaNota = (kidAlvo) => {
    const me = Number(rec?.rec_id) || 0;
    const alvo = Number(kidAlvo) || 0;
    const extras = (composeRecIds || []).map((id) => Number(id)).filter((id) => {
      if (!(id > 0) || id === me) return false;
      const row = (recs || []).find((r) => Number(r?.rec_id) === id);
      if (!row || !isLiberado(row) || isFaturado(row)) return false;
      const existente = Number(row?.kamino_id) || 0;
      if (alvo > 0 && existente > 0 && existente !== alvo) return false;
      return true;
    });
    return [me, ...extras].filter((id, idx, arr) => id > 0 && arr.indexOf(id) === idx);
  };

  const aplicarNfEmitida = async (info) => {
    const numero = info?.numero ? ` nº ${info.numero}` : '';
    const situacao = info?.situacao ? ` (${info.situacao})` : '';
    const kid = Number(info?.kamino_id) || 0;
    const ids = idsSelecionadosNaNota(kid);
    const varias = ids.length > 1;
    const ok = window.confirm(
      `A NFS-e ${info.kamino_id}${numero} já foi emitida na Kamino${situacao}. `
      + (varias
        ? `Confirmar grava essa nota no Oikos e vincula as ${ids.length} receitas selecionadas. `
        : 'Confirmar grava essa nota no Oikos e vincula esta receita. ')
      + (varias
        ? 'Descrição, valor, tomador, prestador, município e código de serviço de cada receita permanecem.'
        : 'Descrição, valor, tomador, prestador, município e código de serviço da receita permanecem.')
    );
    if (!ok) {
      setKaminoIdLocal(Number(rec?.kamino_id) > 0 ? Number(rec.kamino_id) : null);
      return false;
    }
    setKaminoIdLocal(kid);
    for (const id of ids) {
      await oikosApi.post('/', { action: 'update', table: 'rec', data: { rec_id: id, kamino_id: kid } });
    }
    if (typeof onRecPatched === 'function') onRecPatched({ rec_id: rec.rec_id, kamino_id: kid });
    pulledIssuedRef.current = kid;
    if (!varias) {
      notifySuccess('Nota da Kamino vinculada. Os dados da receita não foram alterados.');
    }
    const hit = await sincronizarNfseDestaReceita({ silent: varias });
    const nfGerada = Number(hit?.nf_id) || 0;
    if (nfGerada > 0) {
      for (const id of ids) {
        if (id === Number(rec?.rec_id)) continue;
        await oikosApi.post('/', {
          action: 'update',
          table: 'rec',
          data: { rec_id: id, nf_id: nfGerada, rec_fat: 1 },
        });
      }
    }
    if (varias) {
      notifySuccess(
        nfGerada > 0
          ? `Nota da Kamino vinculada a ${ids.length} receitas. Os dados de cada receita não foram alterados.`
          : `ID Kamino gravado em ${ids.length} receitas. A nota ainda não foi sincronizada.`
      );
      setComposeRecIds([]);
      if (typeof onNeedReload === 'function') onNeedReload();
    }
    return true;
  };

  const consultarIdKamino = async (idInformado) => {
    const id = Number(idInformado || kaminoIdLocal) || 0;
    if (id <= 0 || !rec?.rec_id) return 'vazio';
    try {
      const info = await lerNfseKamino({ id });
      if (!info.emitida) return 'rascunho';
      const mesmoId = id === Number(rec?.kamino_id || 0);
      const jaSync = Number(nfId || rec?.nf_id) > 0 || Number(recFatLocal ?? rec?.rec_fat) === 1;
      if (mesmoId && jaSync) return 'igual';
      if (mesmoId) {
        const ok = await sincronizarNfseDestaReceita({ silent: false });
        return ok ? 'emitida' : 'aguardando';
      }
      await aplicarNfEmitida(info);
      return 'emitida';
    } catch (e) {
      notifyError(e?.message || 'Não encontrei essa NFS-e na Kamino.');
      setKaminoIdLocal(Number(rec?.kamino_id) > 0 ? Number(rec.kamino_id) : null);
      return 'erro';
    }
  };

  if (!rec) return null;

  const recFatShown = recFatLocal ?? rec.rec_fat;
  const dtPrev = parseDate(rec?.rec_dtNfPrev);
  const today = startOfToday();
  const diasParaLimite = dtPrev ? diffDays(dtPrev, today) : null;
  const situacao = getSituacao(rec, today);

  const kaminoStatus = getKaminoEmitStatus({
    ...rec,
    kamino_id: kaminoIdLocal ?? rec.kamino_id,
    nf_id: nfId ?? rec.nf_id,
    rec_fat: recFatShown,
  });
  const emitDraft = {
    ...rec,
    rec_NFDescricao: nfDescricao,
    kamino_imposto: kaminoImposto,
    contrato_codTrib: codTrib,
    kamino_id: kaminoIdLocal,
    nf_id: nfId,
    rec_fat: recFatShown,
    pj_tomador: pjTomadorId,
    pj_prestador: pjPrestadorId,
    mun_id: munId,
    retTemplate_id: retTemplateId,
    pjTomadorKaminoId: pjTomadorKaminoLocal ?? rec.pjTomadorKaminoId,
    pjPrestadorKaminoId: pjPrestadorKaminoLocal ?? rec.pjPrestadorKaminoId,
  };
  const guardrail = avaliarGuardrailNf({
    rec: emitDraft,
    nfDescricao,
    fatProc: fatProcOrientacao,
    template: nfDescTpl,
  });
  const emitMissing = [...getEmitMissingFields(emitDraft), ...guardrail.missing]
    .filter((item, idx, arr) => arr.indexOf(item) === idx);
  const notaJaEmitida = kaminoStatus === 'sincronizado';
  const canShowEmitForm = canEditEmit && isLiberado(rec) && Number(recFatShown) !== 1 && !notaJaEmitida;
  const canClickEmit = canShowEmitForm && kaminoStatus === 'nao_enviado' && emitMissing.length === 0 && !emitting && !savingNfFields && !syncingNfse;
  const canClickUpdate = canShowEmitForm && kaminoStatus === 'aguardando' && emitMissing.length === 0 && !emitting && !savingNfFields && !syncingNfse;

  const recParaGrupo = { ...rec, nf_id: nfId ?? rec?.nf_id, kamino_id: kaminoIdLocal ?? rec?.kamino_id, rec_fat: recFatShown, pj_tomador: pjTomadorId ?? rec?.pj_tomador };
  const grupoAtual = recsDoMesmoGrupoNf(recs, recParaGrupo);
  const candidatosCompose = recsComponiveisCom(recs, recParaGrupo);
  const composeExtra = (composeRecIds || []).map((id) => Number(id)).filter((id) => id && id !== Number(rec?.rec_id));
  const brutoComposto = num(rec?.rec_vlrBru) + composeExtra.reduce((acc, id) => {
    const row = (recs || []).find((r) => Number(r?.rec_id) === id);
    return acc + num(row?.rec_vlrBru);
  }, 0);

  const getLatestDocByTp = (tpId) => {
    const filtered = (docs || []).filter((d) => Number(d?.tpDocumento_id) === Number(tpId));
    if (filtered.length === 0) return null;
    filtered.sort((a, b) => Number(b?.arquivo_id || 0) - Number(a?.arquivo_id || 0));
    return filtered[0];
  };

  const xmlDoc = getLatestDocByTp(TP_DOC_NF_XML);
  const pdfDoc = getLatestDocByTp(TP_DOC_NF_DANFE);

  const openDoc = (doc) => {
    const path = doc?.arquivo_caminho;
    if (!path) return;
    window.open(`${OIKOS_FILES_BASE}${path}`, '_blank');
  };

  const handleUploadXml = async (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file) return;
    if (!rec?.rec_id) {
      notifyError('rec_id não encontrado.');
      return;
    }
    setUploadingXml(true);
    try {
      const xmlText = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('Falha ao ler XML.'));
        reader.readAsText(file);
      });

      const nfNum = extractNfNumFromXmlText(xmlText);
      if (!nfNum) throw new Error('Não consegui identificar o número da NF no XML.');

      if (nfId) {
        // NF já existe: atualiza o XML (nf_xml) também.
        await oikosApi.post('/', {
          action: 'update',
          table: 'nf',
          data: { nf_id: nfId, nf_xml: xmlText },
        });
        await uploadFileToDocumento({ tpDocumentoId: TP_DOC_NF_XML, tabRef: 'nf', tabId: nfId, file, documentoDesc: `XML NF ${nfNum}` });
        notifySuccess('XML anexado com sucesso na NF vinculada.');
        await refreshDocs(nfId);
        if (onNeedReload) onNeedReload();
        return;
      }

      const fd = new FormData();
      fd.append('arquivos', file, getFileName(file));
      let newNfId = null;
      let nfNumFromApi = null;
      let nfCompFromApi = null;

      try {
        const upRes = await fetch(OIKOS_NF_UP_ENDPOINT, { method: 'POST', body: fd, credentials: 'include' });
        if (!upRes.ok) {
          const t = await upRes.text().catch(() => '');
          throw new Error(`Falha ao enviar XML para processamento (${upRes.status}). ${t}`);
        }
        const payload = await upRes.json().catch(() => null);
        if (!payload || payload.ok !== true) throw new Error(payload?.message || 'O endpoint de processamento não retornou ok=true.');
        newNfId = payload?.nf_id ?? null;
        nfNumFromApi = payload?.nf_num ?? null;
        if (!newNfId) throw new Error('nf_id não retornado pelo endpoint.');
      } catch (e) {
        const ensured = await ensureNfFromXml({ xmlText, nfNum });
        newNfId = ensured.nf_id;
        nfNumFromApi = ensured.nf_num;
        nfCompFromApi = ensured.nf_comp ?? null;
      }

      const nfInfoRes = await oikosApi.get(
        `/?${qs.stringify({ action: 'get', table: 'nf', data: { nf_id: newNfId, nf_ativo: 1 } })}`
      );
      const nfInfo = Array.isArray(nfInfoRes?.data) ? nfInfoRes.data[0] : null;
      const nfComp = nfInfo?.nf_comp || nfCompFromApi || null;

      await oikosApi.post('/', {
        action: 'update',
        table: 'rec',
        data: { rec_id: rec.rec_id, nf_id: newNfId, rec_fat: 1, ...(nfComp ? { rec_dtNfPrev: nfComp } : {}) },
      });

      // Garante que o conteúdo do XML fique salvo em nf.nf_xml.
      await oikosApi.post('/', {
        action: 'update',
        table: 'nf',
        data: { nf_id: newNfId, nf_xml: xmlText },
      });

      setNfId(newNfId);
      setRecFatLocal(1);
      if (typeof onRecPatched === 'function') {
        onRecPatched({ nf_id: newNfId, rec_fat: 1, ...(nfComp ? { rec_dtNfPrev: nfComp } : {}) });
      }

      await uploadFileToDocumento({ tpDocumentoId: TP_DOC_NF_XML, tabRef: 'nf', tabId: newNfId, file, documentoDesc: `XML NF ${nfNumFromApi || nfNum}` });
      notifySuccess('XML processado/anexado com sucesso.');
      await refreshDocs(newNfId);
      if (onNeedReload) onNeedReload();
    } catch (e) {
      notifyError(e?.message || 'Falha ao anexar XML.');
    } finally {
      setUploadingXml(false);
    }
  };

  const handleUploadPdf = async (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file) return;
    if (!nfId) {
      notifyError('Envie o XML primeiro (nf_id ainda não definido).');
      return;
    }
    if (!xmlDoc?.arquivo_caminho) {
      notifyError('Envie/anexe o XML primeiro.');
      return;
    }

    setUploadingPdf(true);
    try {
      await uploadFileToDocumento({ tpDocumentoId: TP_DOC_NF_DANFE, tabRef: 'nf', tabId: nfId, file, documentoDesc: `DANFE ${getFileName(file)}` });
      notifySuccess('PDF (DANFE) anexado com sucesso.');
      await refreshDocs(nfId);
      if (onNeedReload) onNeedReload();
    } catch (e) {
      notifyError(e?.message || 'Falha ao anexar PDF.');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleSaveNfFields = async ({ quiet } = {}) => {
    if (!rec?.rec_id) {
      notifyError('rec_id não encontrado.');
      throw new Error('rec_id não encontrado.');
    }
    setSavingNfFields(true);
    try {
      const patch = {
        rec_id: rec.rec_id,
        rec_NFDescricao: String(nfDescricao || '').trim(),
        kamino_imposto: String(kaminoImposto || '').trim(),
        contrato_codTrib: String(codTrib || '').trim(),
        pj_tomador: pjTomadorId || null,
        pj_prestador: pjPrestadorId || null,
        mun_id: munId || null,
        retTemplate_id: retTemplateId || null,
        projFatTpl_id: projFatTplId || null,
        kamino_id: Number(kaminoIdLocal) > 0 ? Number(kaminoIdLocal) : null,
      };
      if (pjTomadorId) {
        const kid = Number(pjTomadorKaminoLocal) > 0 ? Number(pjTomadorKaminoLocal) : null;
        await oikosApi.post('/', {
          action: 'update',
          table: 'pj',
          data: { pj_id: pjTomadorId, kamino_id: kid },
        });
        setPjOptions((prev) => prev.map((o) => (
          Number(o.value) === Number(pjTomadorId) ? { ...o, kamino_id: kid } : o
        )));
      }
      await oikosApi.post('/', { action: 'update', table: 'rec', data: patch });
      if (typeof onRecPatched === 'function') {
        onRecPatched({
          rec_NFDescricao: patch.rec_NFDescricao,
          kamino_imposto: patch.kamino_imposto,
          contrato_codTrib: patch.contrato_codTrib,
          pj_tomador: patch.pj_tomador,
          pj_prestador: patch.pj_prestador,
          mun_id: patch.mun_id,
          retTemplate_id: patch.retTemplate_id,
          projFatTpl_id: patch.projFatTpl_id,
          kamino_id: patch.kamino_id,
          pjTomadorNome: pjTomadorNomeLocal,
          pjTomadorKaminoId: pjTomadorKaminoLocal,
          pjPrestadorNome: pjPrestadorNomeLocal,
          pjPrestadorKaminoId: pjPrestadorKaminoLocal,
        });
      }
      if (!quiet) notifySuccess('Dados da NF salvos.');
      return patch;
    } catch (e) {
      notifyError(e?.message || 'Falha ao salvar dados da NF.');
      throw e;
    } finally {
      setSavingNfFields(false);
    }
  };

  const persistTomadorKaminoId = async (kid) => {
    if (!canEditEmit) return;
    if (!pjTomadorId) {
      notifyError('Selecione o tomador para gravar o ID Kamino.');
      return;
    }
    const val = kid == null || kid === '' ? null : Number(kid);
    if (val != null && (!Number.isFinite(val) || val <= 0)) {
      notifyError('ID Kamino do tomador inválido.');
      return;
    }
    await oikosApi.post('/', {
      action: 'update',
      table: 'pj',
      data: { pj_id: pjTomadorId, kamino_id: val },
    });
    if (rec?.rec_id) {
      await oikosApi.post('/', {
        action: 'update',
        table: 'rec',
        data: { rec_id: rec.rec_id, pj_tomador: pjTomadorId },
      });
    }
    setPjTomadorKaminoLocal(val);
    setPjOptions((prev) => prev.map((o) => (
      Number(o.value) === Number(pjTomadorId) ? { ...o, kamino_id: val } : o
    )));
    if (typeof onRecPatched === 'function') {
      onRecPatched({ pj_tomador: pjTomadorId, pjTomadorKaminoId: val, pjTomadorNome: pjTomadorNomeLocal });
    }
  };

  const handleEmitKamino = async () => {
    if (!rec?.rec_id) {
      notifyError('rec_id não encontrado.');
      return;
    }
    if (Number(nfId || rec?.nf_id) > 0 || Number(recFatLocal ?? rec?.rec_fat) === 1) {
      notifyError('Esta nota já foi emitida e não pode ser alterada.');
      return;
    }
    const idKamino = Number(kaminoIdLocal || rec?.kamino_id) || 0;
    if (idKamino > 0) {
      const resultado = await consultarIdKamino(idKamino);
      if (resultado === 'emitida' || resultado === 'erro' || resultado === 'aguardando' || resultado === 'igual') {
        if (resultado === 'igual') {
          notifyError('Esta NFS-e já foi emitida na Kamino. O Oikos não envia alterações para ela.');
        }
        return;
      }
    }
    const draftRec = {
      ...rec,
      rec_NFDescricao: nfDescricao,
      kamino_imposto: kaminoImposto,
      contrato_codTrib: codTrib,
      kamino_id: kaminoIdLocal,
      nf_id: nfId,
      rec_fat: recFatLocal ?? rec.rec_fat,
      pj_tomador: pjTomadorId,
      pj_prestador: pjPrestadorId,
      mun_id: munId,
      retTemplate_id: retTemplateId,
      pjTomadorKaminoId: pjTomadorKaminoLocal ?? rec.pjTomadorKaminoId,
      pjPrestadorKaminoId: pjPrestadorKaminoLocal ?? rec.pjPrestadorKaminoId,
    };
    const missing = [
      ...getEmitMissingFields(draftRec),
      ...avaliarGuardrailNf({
        rec: draftRec,
        nfDescricao,
        fatProc: fatProcOrientacao,
        template: nfDescTpl,
      }).missing,
    ].filter((item, idx, arr) => arr.indexOf(item) === idx);
    if (missing.length) {
      notifyError(`Não é possível emitir: ${missing.join('; ')}.`);
      return;
    }
    const pedidoId = Number(kaminoIdLocal || rec?.kamino_id) || 0;
    const nSel = 1 + composeExtra.length;
    const confirmMsg = pedidoId > 0
      ? `Atualizar o pedido ${pedidoId} na Kamino com estes dados?`
      : (nSel > 1
        ? `Enviar NFS-e composta por ${nSel} receitas (bruto ${currencyFormatter.format(brutoComposto)})?`
        : 'Enviar esta receita a Kamino para emissao da NFS-e?');
    if (!window.confirm(confirmMsg)) return;

    setEmitting(true);
    try {
      try {
        await handleSaveNfFields({ quiet: true });
      } catch {
        return;
      }
      const recIdsGrupo = [Number(rec.rec_id), ...composeRecIds.map((id) => Number(id))].filter((id, idx, arr) => id > 0 && arr.indexOf(id) === idx);
      const payload = await emitNfse({ rec_id: rec.rec_id, rec_ids: recIdsGrupo });
      const kid = payload?.kamino_id ?? pedidoId ?? null;
      setKaminoIdLocal(kid);
      if (typeof onRecPatched === 'function') onRecPatched({ kamino_id: kid });
      notifySuccess(payload?.message || (pedidoId > 0 ? 'Pedido atualizado na Kamino.' : 'Pedido enviado à Kamino.'));
      if (onNeedReload) onNeedReload();
    } catch (e) {
      notifyError(e?.message || 'Falha ao enviar pedido à Kamino.');
    } finally {
      setEmitting(false);
    }
  };

  const recDraftForFat = {
    ...rec,
    nf_id: nfId ?? rec?.nf_id,
    rec_fat: recFatLocal ?? rec?.rec_fat,
  };
  const recEstaFaturada = isRecFaturada(recDraftForFat);

  const handleConfirmDesfazerFaturamento = async () => {
    if (!canUndoFat) {
      notifyError('Sem permissão para desfazer faturamento.');
      return;
    }
    setUndoLoading(true);
    try {
      const result = await desfazerFaturamentoReceita({ rec: recDraftForFat });
      setNfId(null);
      setRecFatLocal(0);
      setNf(null);
      setDocs([]);
      setLinkNfSelected(null);
      if (typeof onRecPatched === 'function') onRecPatched(result.patch);
      notifySuccess(
        result.docsPuladosPorOutrosRec
          ? 'Faturamento desfeito. XML/PDF mantidos: outra receita ainda usa esta NF.'
          : 'Faturamento desfeito. NF desvinculada e anexos XML/PDF inativados.'
      );
      setUndoOpen(false);
      if (onNeedReload) onNeedReload();
    } catch (e) {
      notifyError(e?.message || 'Falha ao desfazer o faturamento.');
    } finally {
      setUndoLoading(false);
    }
  };

  const handleVincularNfExistente = async () => {
    if (!canUndoFat) {
      notifyError('Sem permissão para vincular NF.');
      return;
    }
    const selectedId = Number(linkNfSelected?.value || 0);
    if (!selectedId) {
      notifyError('Selecione uma NF existente.');
      return;
    }
    setLinkNfSaving(true);
    try {
      const result = await vincularNfExistente({
        rec: recDraftForFat,
        nfId: selectedId,
        nfComp: linkNfSelected?.nf_comp,
      });
      setNfId(result.nfId);
      setRecFatLocal(1);
      setNf(result.nf);
      if (typeof onRecPatched === 'function') onRecPatched(result.patch);
      notifySuccess(`NF ${result.nf?.nf_num || result.nfId} vinculada. Receita marcada como faturada.`);
      setLinkNfSelected(null);
      await refreshDocs(result.nfId);
      if (onNeedReload) onNeedReload();
    } catch (e) {
      notifyError(e?.message || 'Falha ao vincular a NF.');
    } finally {
      setLinkNfSaving(false);
    }
  };

  const handleAnexarRecsNaNf = async () => {
    const nfTarget = Number(nfId || rec?.nf_id) || 0;
    if (nfTarget <= 0) {
      notifyError('Esta receita ainda nao tem NF para amarrar.');
      return;
    }
    const ids = (attachRecIds || []).map((id) => Number(id)).filter((id) => id > 0);
    if (!ids.length) {
      notifyError('Selecione ao menos uma receita liberada.');
      return;
    }
    setAttachingRecs(true);
    try {
      const kid = Number(kaminoIdLocal || rec?.kamino_id) || 0;
      for (const id of ids) {
        const sibling = (recs || []).find((r) => Number(r?.rec_id) === id);
        if (!sibling) continue;
        await vincularNfExistente({ rec: sibling, nfId: nfTarget, nfComp: nf?.nf_comp });
        if (kid > 0) {
          await oikosApi.post('/', { action: 'update', table: 'rec', data: { rec_id: id, kamino_id: kid } });
        }
      }
      notifySuccess(ids.length + ' receita(s) amarradas a esta NF (XML/PDF ficam na nota).');
      setAttachRecIds([]);
      if (onNeedReload) onNeedReload();
    } catch (e) {
      notifyError(e?.message || 'Falha ao amarrar receitas nesta NF.');
    } finally {
      setAttachingRecs(false);
    }
  };

  const dropStyle = {
    cursor: 'pointer',
    borderWidth: 2,
    borderRadius: 6,
    borderColor: '#ccc',
    borderStyle: 'dashed',
    backgroundColor: '#f8f8f8',
    outline: 'none',
    transition: 'all .2s ease-in-out',
    textAlign: 'center',
    padding: '10px 12px',
    fontSize: 12,
    color: '#666',
  };

  const compactDropStyle = { ...dropStyle, padding: '6px 10px', fontSize: 11, width: 210 };

  return (
    <div className="ml-4" style={{ width: '100%' }}>
      <SimpleCard
        standard={false}
        title={<h4 className="text-truncate font-size-14 mb-2">{`Detalhes: ${rec.rec_ep || 'EP'}`}</h4>}
        text={
          <div className="d-flex flex-column" style={{ gap: 10, fontSize: 13 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: '#6c757d', fontSize: 12 }}>Projeto</div>
                  <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {rec.recProjeto || '-'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <SituacaoPill value={situacao} />
                  {diasParaLimite != null && !isFaturado(rec) && (
                    <span style={{ fontSize: 12, fontWeight: 800, color: diasParaLimite < 0 ? '#ff5757' : '#6c757d' }}>
                      {`${diasParaLimite}d`}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <LabelValue label="Bruto" value={currencyFormatter.format(num(rec.rec_vlrBru))} />
                {composeExtra.length > 0 ? (
                  <LabelValue label="Bruto da NF" value={currencyFormatter.format(brutoComposto)} />
                ) : null}
                <LabelValue label="Líquido" value={currencyFormatter.format(num(rec.rec_vlrLiq))} />
                <LabelValue label="Limite" value={fmtDateBr(rec.rec_dtNfPrev)} />
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <LabelValue label="Liberado" value={isLiberado(rec) ? 'Sim' : 'Não'} />
                <LabelValue label="Faturado" value={Number(recFatShown) === 1 ? 'Sim' : 'Não'} />
                <LabelValue label="Risco" value={rec.rec_risco ?? '-'} />
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <LabelValue label="Quit. ini" value={fmtDateBr(rec.rec_dtQuitIni)} />
                <LabelValue label="Quit. atual" value={fmtDateBr(rec.rec_dtQuitAtual)} />
                <LabelValue label="rec_id" value={rec.rec_id} />
              </div>

              <div>
                {libDocs.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setEvidenciaOpen(true)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: 0,
                      border: 0,
                      background: 'none',
                      color: '#1C47A4',
                      fontWeight: 800,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    <i className="ri-file-pdf-line" aria-hidden="true" />
                    Evidência de autorização
                    {libDocs.length > 1 ? ` (${libDocs.length})` : ''}
                  </button>
                ) : (
                  <span style={{ fontSize: 12, color: '#9aa3ad' }}>Sem evidência de autorização</span>
                )}
              </div>
            </div>

            <hr className="my-2" />

            <div className="d-flex flex-column" style={{ gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 260 }}>
                  <div style={{ fontWeight: 800 }}>Nota Fiscal</div>
                  {podeVerNf && (
                    <div style={{ fontWeight: 900, color: '#4e515e' }}>{nf?.nf_num || (nfLoading ? 'Carregando...' : '-')}</div>
                  )}
                  {podeVerNf && nfId != null && (
                    <IconButton size="small" title="Visualizar Nota Fiscal" onClick={openNfModal}>
                      <i className="ri-file-search-line font-size-18" />
                    </IconButton>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <KaminoStatusPill status={kaminoStatus} />
                  {podeVerNf && canUndoFat && recEstaFaturada && (
                    <Button
                      size="small"
                      color="warning"
                      variant="outlined"
                      disabled={undoLoading}
                      onClick={() => setUndoOpen(true)}
                    >
                      Desfazer faturamento
                    </Button>
                  )}
                </div>
              </div>

              {canShowLinkNf && (
                <div
                  style={{
                    border: '1px solid rgba(78,81,94,0.18)',
                    borderRadius: 6,
                    padding: 10,
                    background: 'rgba(78,81,94,0.03)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <div style={{ fontWeight: 800, fontSize: 13 }}>Vincular NF já existente</div>
                  <div style={{ fontSize: 12, color: '#6c757d' }}>
                    Associa uma nota fiscal já cadastrada a esta receita e marca como faturada.
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ minWidth: 260, flex: 1 }}>
                      <SearchableSelect
                        options={linkNfOptions}
                        isLoading={linkNfLoading}
                        isClearable
                        placeholder="Buscar NF por número ou id..."
                        value={linkNfSelected}
                        onChange={(opt) => setLinkNfSelected(opt)}
                        styles={{ container: (base) => ({ ...base, fontSize: 12 }) }}
                      />
                    </div>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={linkNfSaving || linkNfLoading || !linkNfSelected}
                      onClick={() => {
                        handleVincularNfExistente().catch(() => {});
                      }}
                    >
                      {linkNfSaving ? 'Vinculando...' : 'Vincular'}
                    </Button>
                    {(linkNfSaving || linkNfLoading) && <Spinner size="sm" />}
                  </div>
                </div>
              )}

              <div
                style={{
                  border: '1px solid rgba(78,81,94,0.18)',
                  borderRadius: 6,
                  padding: 10,
                  background: 'rgba(78,81,94,0.03)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 13 }}>Emissão Kamino</div>
                {kaminoStatus === 'aguardando' && (
                  <div style={{ fontSize: 12, color: '#b46900' }}>
                    Pedido enviado (ID {kaminoIdLocal || rec.kamino_id}). Altere os campos e use Atualizar na Kamino.
                    A liberação da nota continua na Kamino. Quando ela confirmar a emissão, o Oikos grava o XML e o PDF.
                  </div>
                )}
                {kaminoStatus === 'sincronizado' && (
                  <div style={{ fontSize: 12, color: '#5664d2' }}>NFS-e sincronizada no Oikos.</div>
                )}
                {grupoAtual.length > 1 && (
                  <div style={{ fontSize: 12, color: '#4e515e' }}>
                    NF conjunta: {grupoAtual.length} receitas
                    {' '}
                    ({grupoAtual.map((r) => r.rec_ep || r.rec_id).join(' ? ')}).
                    XML e PDF ficam na nota; evidencia de liberacao permanece em cada receita.
                  </div>
                )}
                {notaJaEmitida && candidatosCompose.length > 0 && (
                  <div
                    style={{
                      border: '1px solid rgba(78,81,94,0.18)',
                      borderRadius: 6,
                      padding: 10,
                      background: 'rgba(78,81,94,0.03)',
                    }}
                  >
                    <div style={{ fontWeight: 800, fontSize: 13 }}>Amarrar mais receitas a esta NF</div>
                    <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 6 }}>
                      Inclui receitas liberadas ainda sem nota. Documentos da NF (XML/PDF) passam a valer para todas.
                    </div>
                    {candidatosCompose.map((r) => {
                      const id = Number(r.rec_id);
                      const checked = attachRecIds.includes(id);
                      return (
                        <label key={'att-' + id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, marginBottom: 4 }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const on = e.target.checked;
                              setAttachRecIds((prev) => (on ? [...prev, id] : prev.filter((x) => x !== id)));
                            }}
                          />
                          <span>
                            {r.rec_ep || ('rec ' + id)}
                            {' ? '}
                            {currencyFormatter.format(num(r.rec_vlrBru))}
                          </span>
                        </label>
                      );
                    })}
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={attachingRecs || attachRecIds.length === 0}
                      onClick={() => {
                        handleAnexarRecsNaNf().catch(() => {});
                      }}
                    >
                      {attachingRecs ? 'Amarrando...' : 'Amarrar a esta NF'}
                    </Button>
                  </div>
                )}

                {canEditEmit && (
                  <label style={{ fontSize: 12, color: '#6c757d', marginBottom: 0, width: '100%' }}>
                    Tomador do serviço
                    <div style={{ marginTop: 4 }}>
                      <SearchableSelect
                        options={pjOptions}
                        isLoading={optionsLoading}
                        isClearable
                        placeholder="Selecione o tomador..."
                        value={pjOptions.find((o) => Number(o.value) === Number(pjTomadorId)) || null}
                        onChange={(opt) => {
                          const newId = opt?.value ?? null;
                          setPjTomadorId(newId);
                          setPjTomadorNomeLocal(opt?.nome || opt?.label || '');
                          setPjTomadorKaminoLocal(opt?.kamino_id ?? null);
                          if (rec?.rec_id && newId) {
                            oikosApi.post('/', {
                              action: 'update',
                              table: 'rec',
                              data: { rec_id: rec.rec_id, pj_tomador: newId },
                            }).catch(() => {});
                          }
                        }}
                        styles={{ container: (base) => ({ ...base, fontSize: 12 }) }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>ID Kamino</span>
                      <input
                        type="number"
                        min="1"
                        value={pjTomadorKaminoLocal ?? ''}
                        onChange={(e) => {
                          const raw = String(e.target.value || '').trim();
                          setPjTomadorKaminoLocal(raw === '' ? null : Number(raw));
                        }}
                        onBlur={() => {
                          persistTomadorKaminoId(pjTomadorKaminoLocal).catch((e) => {
                            notifyError(e?.message || 'Falha ao gravar o ID Kamino do tomador.');
                          });
                        }}
                        placeholder="Código da pessoa na Kamino"
                        style={{
                          width: 160,
                          fontSize: 12,
                          padding: 6,
                          borderRadius: 4,
                          border: `1px solid ${pjTomadorKaminoLocal ? '#ced4da' : '#dc3545'}`,
                        }}
                      />
                      {pjTomadorKaminoLocal ? (
                        <span style={{ color: '#1cbb8c', fontSize: 11 }}>OK</span>
                      ) : (
                        <span style={{ color: '#dc3545', fontSize: 11 }}>sem ID Kamino</span>
                      )}
                    </div>
                  </label>
                )}

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12 }}>
                  {!canShowEmitForm && !canEditEmit && (
                    <span>
                      Tomador:{' '}
                      <strong>{pjTomadorNomeLocal || rec.pjTomadorNome || '-'}</strong>
                      {(pjTomadorKaminoLocal ?? rec.pjTomadorKaminoId) ? (
                        <span style={{ color: '#1cbb8c', marginLeft: 6 }}>OK</span>
                      ) : (
                        <span style={{ color: '#dc3545', marginLeft: 6 }}>sem ID Kamino</span>
                      )}
                    </span>
                  )}
                  {!canShowEmitForm && (
                    <span>
                      Prestador:{' '}
                      <strong>{pjPrestadorNomeLocal || rec.pjPrestadorNome || '-'}</strong>
                      {(pjPrestadorKaminoLocal ?? rec.pjPrestadorKaminoId) ? (
                        <span style={{ color: '#1cbb8c', marginLeft: 6 }}>OK</span>
                      ) : (
                        <span style={{ color: '#dc3545', marginLeft: 6 }}>sem ID Kamino</span>
                      )}
                    </span>
                  )}
                </div>

                {canShowEmitForm && (
                  <>
                    
                                        <label style={{ fontSize: 12, color: '#6c757d', marginBottom: 0, width: '100%' }}>
                      Serie de NFS-e (cidade de prestacao)
                      <div style={{ marginTop: 4 }}>
                        <SearchableSelect
                          options={(fatTpls || []).map((tpl) => ({
                            value: Number(tpl.projFatTpl_id),
                            label: (tpl.projFatTpl_nome || 'Serie') + (munOptions.find((o) => Number(o.value) === Number(tpl.mun_id)) ? (' — ' + munOptions.find((o) => Number(o.value) === Number(tpl.mun_id)).label) : ''),
                            tpl,
                          }))}
                          isClearable
                          placeholder="Selecione a serie..."
                          value={(fatTpls || []).map((tpl) => ({
                            value: Number(tpl.projFatTpl_id),
                            label: (tpl.projFatTpl_nome || 'Serie') + (munOptions.find((o) => Number(o.value) === Number(tpl.mun_id)) ? (' — ' + munOptions.find((o) => Number(o.value) === Number(tpl.mun_id)).label) : ''),
                            tpl,
                          })).find((o) => Number(o.value) === Number(projFatTplId)) || null}
                          onChange={(opt) => {
                            const tpl = opt?.tpl;
                            if (!tpl) {
                              setProjFatTplId(null);
                              setProjFatTplNome('');
                              return;
                            }
                            setProjFatTplId(Number(tpl.projFatTpl_id));
                            setProjFatTplNome(String(tpl.projFatTpl_nome || ''));
                            if (tpl.mun_id) setMunId(Number(tpl.mun_id));
                            const desc = String(tpl.projFatTpl_nfDescTpl || '').trim();
                            if (desc) {
                              setNfDescTpl(desc);
                              setNfDescricao(preencherTemplateNfDescricao(desc, rec, []));
                            }
                          }}
                          styles={{ container: (base) => ({ ...base, fontSize: 12 }) }}
                        />
                      </div>
                      <div style={{ marginTop: 4, fontSize: 11 }}>
                        Uma serie por municipio de prestacao. Mesmo CNPJ em cidades diferentes = series diferentes. Crie e edite no processo de faturamento do projeto.
                      </div>
                    </label>

{sugestaoOrigem.length > 0 && (
                      <div style={{ fontSize: 12, color: '#5664d2' }}>
                        Sugestão a partir do projeto e do contrato: {sugestaoOrigem.join('; ')}. Salvar grava estes dados neste evento.
                      </div>
                    )}
                    {candidatosCompose.length > 0 && (
                      <div
                        style={{
                          border: '1px solid rgba(86,100,210,0.25)',
                          borderRadius: 6,
                          padding: 10,
                          background: 'rgba(86,100,210,0.04)',
                        }}
                      >
                        <div style={{ fontWeight: 800, fontSize: 13 }}>Compor a mesma NFS-e</div>
                        <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 6 }}>
                          Receitas liberadas do mesmo projeto. O valor da nota e a soma; XML/PDF ficam na NF gerada.
                        </div>
                        {candidatosCompose.map((r) => {
                          const id = Number(r.rec_id);
                          const checked = composeRecIds.includes(id);
                          return (
                            <label key={id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, marginBottom: 4 }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const on = e.target.checked;
                                  setComposeRecIds((prev) => (on ? [...prev, id] : prev.filter((x) => x !== id)));
                                }}
                              />
                              <span>
                                {r.rec_ep || ('rec ' + id)}
                                {' ? '}
                                {currencyFormatter.format(num(r.rec_vlrBru))}
                              </span>
                            </label>
                          );
                        })}
                        {composeExtra.length > 0 && (
                          <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4 }}>
                            Total da NF: {currencyFormatter.format(brutoComposto)} ({1 + composeExtra.length} receitas)
                          </div>
                        )}
                      </div>
                    )}
                    <label style={{ fontSize: 12, color: '#6c757d', marginBottom: 0, width: 220 }}>
                      ID da NFS-e na Kamino
                      <input
                        type="number"
                        min="1"
                        value={kaminoIdLocal ?? ''}
                        onChange={(e) => {
                          const raw = String(e.target.value || '').trim();
                          setKaminoIdLocal(raw === '' ? null : Number(raw));
                        }}
                        onBlur={() => {
                          consultarIdKamino(kaminoIdLocal).catch(() => {});
                        }}
                        style={{
                          display: 'block',
                          width: '100%',
                          marginTop: 4,
                          fontSize: 12,
                          padding: 8,
                          borderRadius: 4,
                          border: '1px solid #ced4da',
                        }}
                      />
                      <div style={{ marginTop: 4, fontSize: 11 }}>
                        Código da nota na Kamino. Marque antes as receitas em Compor a mesma NFS-e. Se a nota já foi emitida, o Oikos cria a nota e vincula esta receita e as marcadas, sem substituir os dados de cada receita.
                      </div>
                    </label>
                    <label style={{ fontSize: 12, color: '#6c757d', marginBottom: 0, width: '100%' }}>
                      Prestador do serviço
                      <div style={{ marginTop: 4 }}>
                        <SearchableSelect
                          options={pjOptions}
                          isLoading={optionsLoading}
                          isClearable
                          placeholder="Selecione o prestador..."
                          value={pjOptions.find((o) => Number(o.value) === Number(pjPrestadorId)) || null}
                          onChange={(opt) => {
                            setPjPrestadorId(opt?.value ?? null);
                            setPjPrestadorNomeLocal(opt?.nome || '');
                            setPjPrestadorKaminoLocal(opt?.kamino_id ?? null);
                          }}
                          styles={{ container: (base) => ({ ...base, fontSize: 12 }) }}
                        />
                      </div>
                      {pjPrestadorId && (
                        <div style={{ marginTop: 4, fontSize: 11, color: pjPrestadorKaminoLocal ? '#1cbb8c' : '#dc3545' }}>
                          {pjPrestadorKaminoLocal ? `ID Kamino: ${pjPrestadorKaminoLocal}` : 'Prestador sem ID Kamino'}
                        </div>
                      )}
                    </label>

                    <label style={{ fontSize: 12, color: '#6c757d', marginBottom: 0, width: '100%' }}>
                      Descrição da NF
                      <textarea
                        value={nfDescricao}
                        onChange={(e) => setNfDescricao(e.target.value)}
                        rows={3}
                        style={{
                          display: 'block',
                          width: '100%',
                          marginTop: 4,
                          fontSize: 12,
                          padding: 8,
                          borderRadius: 4,
                          border: '1px solid #ced4da',
                        }}
                      />
                      {String(rec?.rec_desc || '').trim() ? (
                        <div style={{ marginTop: 6, fontSize: 11, color: '#495057' }}>
                          Descrição da receita: {rec.rec_desc}
                        </div>
                      ) : null}
                      {(guardrail.extras.po || guardrail.extras.itm) ? (
                        <div style={{ marginTop: 4, fontSize: 11, color: '#1cbb8c' }}>
                          {guardrail.extras.po ? `PO lida: ${guardrail.extras.po}` : ''}
                          {guardrail.extras.po && guardrail.extras.itm ? ' · ' : ''}
                          {guardrail.extras.itm ? `ITM lida: ${guardrail.extras.itm}` : ''}
                        </div>
                      ) : null}
                      {guardrail.avisos.length > 0 && (
                        <div style={{ marginTop: 4, fontSize: 11, color: '#b46900' }}>
                          {guardrail.avisos.join(' ')}
                        </div>
                      )}
                      {fatProcOrientacao ? (
                        <div style={{ marginTop: 6, fontSize: 11, color: '#5664d2', whiteSpace: 'pre-wrap' }}>
                          Processo de faturamento: {fatProcOrientacao}
                        </div>
                      ) : (
                        <div style={{ marginTop: 6, fontSize: 11 }}>
                          {`{núm do mês}`} e {`{mês/ano do BM}`} saem da descrição da receita (ex. «Mês 11. Setembro/2026.»). PO/ITM por nota só se o processo de faturamento exigir. O sistema não inventa valor.
                        </div>
                      )}
                    </label>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <label style={{ fontSize: 12, color: '#6c757d', flex: 1, minWidth: 180, marginBottom: 0 }}>
                        Tabela imposto
                        <div style={{ marginTop: 4 }}>
                          <SearchableSelect
                            options={impostoOptions}
                            isLoading={optionsLoading}
                            isClearable
                            placeholder="Selecione..."
                            value={impostoOptions.find((o) => String(o.value) === String(kaminoImposto)) || null}
                            onChange={(opt) => {
                              setKaminoImposto(opt ? String(opt.value) : '');
                              setRetTemplateId(opt?.retTemplate_id ?? null);
                            }}
                            styles={{ container: (base) => ({ ...base, fontSize: 12 }) }}
                          />
                        </div>
                      </label>
                      <label style={{ fontSize: 12, color: '#6c757d', flex: 1, minWidth: 160, marginBottom: 0 }}>
                        Código de serviço
                        <div style={{ marginTop: 4 }}>
                          <CreatableSelect
                            options={codTribOptions}
                            isClearable
                            placeholder="Selecione ou digite..."
                            formatCreateLabel={(input) => `Usar código "${input}"`}
                            value={
                              codTrib
                                ? codTribOptions.find((o) => String(o.value) === String(codTrib)) || {
                                    value: String(codTrib),
                                    label: String(codTrib),
                                  }
                                : null
                            }
                            onChange={(opt) => {
                              const v = opt ? String(opt.value) : '';
                              setCodTrib(v);
                              if (v && !codTribOptions.some((o) => String(o.value) === v)) {
                                setCodTribOptions((prev) => [...prev, { value: v, label: v }]);
                              }
                            }}
                            styles={{ container: (base) => ({ ...base, fontSize: 12 }) }}
                          />
                        </div>
                      </label>
                    </div>

                    <label style={{ fontSize: 12, color: '#6c757d', marginBottom: 0, width: '100%' }}>
                      Município de prestação
                      <div style={{ marginTop: 4 }}>
                        <SearchableSelect
                          options={munOptions}
                          isLoading={optionsLoading}
                          isClearable
                          placeholder="Selecione o município..."
                          value={munOptions.find((o) => Number(o.value) === Number(munId)) || null}
                          onChange={(opt) => {
                            const next = opt?.value ?? null;
                            setMunId(next);
                            const hits = (fatTpls || []).filter((tpl) => Number(tpl.mun_id) === Number(next));
                            if (hits.length === 1) {
                              const tpl = hits[0];
                              setProjFatTplId(Number(tpl.projFatTpl_id));
                              setProjFatTplNome(String(tpl.projFatTpl_nome || ''));
                              const desc = String(tpl.projFatTpl_nfDescTpl || '').trim();
                              if (desc) {
                                setNfDescTpl(desc);
                                setNfDescricao(preencherTemplateNfDescricao(desc, rec, []));
                              }
                            } else if (hits.length === 0) {
                              setProjFatTplId(null);
                              setProjFatTplNome('');
                            }
                          }}
                          styles={{ container: (base) => ({ ...base, fontSize: 12 }) }}
                        />
                      </div>
                    </label>

                    {cnoProjeto ? (
                      <div style={{ fontSize: 12, color: '#6c757d' }}>
                        CNO do projeto: <strong>{cnoProjeto}</strong>
                      </div>
                    ) : null}

                    {emitMissing.length > 0 && kaminoStatus === 'nao_enviado' && (
                      <div style={{ fontSize: 12, color: '#dc3545' }}>
                        Pendências: {emitMissing.join('; ')}.
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={savingNfFields || emitting}
                        onClick={() => {
                          handleSaveNfFields().catch(() => {});
                        }}
                      >
                        {savingNfFields ? 'Salvando...' : 'Salvar dados da NF'}
                      </Button>
                      {kaminoStatus === 'nao_enviado' && (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!canClickEmit}
                          onClick={() => {
                            handleEmitKamino().catch(() => {});
                          }}
                        >
                          {emitting ? 'Enviando...' : 'Enviar para emissão'}
                        </Button>
                      )}
                      {kaminoStatus === 'aguardando' && (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!canClickUpdate}
                          onClick={() => {
                            handleEmitKamino().catch(() => {});
                          }}
                        >
                          {emitting ? 'Atualizando...' : 'Atualizar na Kamino'}
                        </Button>
                      )}
                      {kaminoStatus === 'aguardando' && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={syncingNfse || emitting || savingNfFields}
                          onClick={() => {
                            sincronizarNfseDestaReceita({ silent: false }).catch(() => {});
                          }}
                        >
                          {syncingNfse ? 'Sincronizando...' : 'Sincronizar NFS-e'}
                        </Button>
                      )}
                      {(savingNfFields || emitting || syncingNfse || optionsLoading) && <Spinner size="sm" />}
                    </div>
                  </>
                )}
              </div>

              {docsError && <div style={{ color: '#dc3545' }}>{docsError}</div>}

              <div style={{ fontSize: 12, color: '#6c757d' }}>
                Quando a Kamino confirmar a emissão da NFS-e deste ID, o XML e o PDF são gravados automaticamente.
              </div>

              {podeVerNf && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 48, color: '#6c757d', fontSize: 12 }}>XML</div>
                  {docsLoading ? (
                    <Spinner size="sm" />
                  ) : xmlDoc?.arquivo_caminho ? (
                    <IconButton size="small" title="Abrir XML" onClick={() => openDoc(xmlDoc)}>
                      <i className="ri-folder-2-line font-size-18" />
                    </IconButton>
                  ) : (
                    <span style={{ fontSize: 12, color: '#9aa3ad' }}>Aguardando a Kamino</span>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 48, color: '#6c757d', fontSize: 12 }}>PDF</div>
                  {docsLoading ? (
                    <Spinner size="sm" />
                  ) : pdfDoc?.arquivo_caminho ? (
                    <IconButton size="small" title="Abrir PDF" onClick={() => openDoc(pdfDoc)}>
                      <i className="ri-folder-2-line font-size-18" />
                    </IconButton>
                  ) : (
                    <span style={{ fontSize: 12, color: '#9aa3ad' }}>Aguardando a Kamino</span>
                  )}
                </div>
                {(xmlDoc?.arquivo_caminho || pdfDoc?.arquivo_caminho) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <FatSupportDocsIcon rec={rec} />
                    <EmailTestButton rec={{ ...rec, nf_id: nfId ?? rec?.nf_id }} label="Enviar e-mail (teste)" />
                    <span style={{ fontSize: 11, color: '#6c757d' }}>Somente bernardo.alcalde@ecossis.com.br, remetente financeiro@ecossis.com.br</span>
                  </div>
                )}
              </div>
              )}
            </div>

            <Dialog open={podeVerNf && nfModalOpen} onClose={closeNfModal} fullWidth maxWidth="md">
              <DialogTitle>{nf ? `Nota Fiscal ${nf.nf_num || ''} (ID: ${nf.nf_id})` : `Nota Fiscal (nf_id: ${rec?.nf_id})`}</DialogTitle>
              <DialogContent dividers>
                {nfLoading && (
                  <Box display="flex" alignItems="center" justifyContent="center" py={3}>
                    <Spinner />
                  </Box>
                )}
                {!nfLoading && nfError && (
                  <Typography color="error" variant="body2">
                    {nfError}
                  </Typography>
                )}
                {!nfLoading && !nfError && nf && (
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <LabelValue label="ID" value={nf.nf_id} />
                      <LabelValue label="Número" value={nf.nf_num || '-'} />
                      <LabelValue label="Status" value={nf.nf_status || '-'} />
                    </Box>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <LabelValue label="Emissão" value={formatDateTime(nf.nf_dtEmissao)} />
                      <LabelValue label="Competência" value={formatDateOnly(nf.nf_comp)} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Descrição
                      </Typography>
                      <Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, padding: 1.5, whiteSpace: 'pre-wrap', fontSize: 13, maxHeight: 240, overflow: 'auto' }}>
                        {nf.nf_desc || '-'}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Valores
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap">
                        <LabelValue label="Serviços" value={formatMoney(nf.nf_vlrServicos)} />
                        <LabelValue label="Base" value={formatMoney(nf.nf_baseCalculo)} />
                        <LabelValue label="Líquido" value={formatMoney(nf.nf_vlrLiq)} />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Tributos (R$)
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap">
                        <LabelValue label="ISS" value={formatMoney(nf.nf_vlrISS)} />
                        <LabelValue label="ISS Ret." value={formatMoney(nf.nf_retISS)} />
                        <LabelValue label="Alíquota" value={formatPercent(nf.nf_aliq)} />
                      </Box>
                    </Box>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={closeNfModal}>Fechar</Button>
              </DialogActions>
            </Dialog>

            <Dialog open={undoOpen} onClose={() => (!undoLoading ? setUndoOpen(false) : null)} fullWidth maxWidth="sm">
              <DialogTitle>Desfazer faturamento</DialogTitle>
              <DialogContent dividers>
                <Typography variant="body2" component="div">
                  Confirma desvincular a NF desta receita?
                  <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 18 }}>
                    <li>
                      <code>rec.nf_id</code> → nulo
                    </li>
                    <li>
                      <code>rec.rec_fat</code> → 0
                    </li>
                    <li>XML/PDF da NF (tipos 39/40) inativados em <code>arquivo</code>/<code>documento</code> se nenhum outro <code>rec</code> usar a mesma NF</li>
                  </ul>
                  A liberação de faturamento (<code>rec_aut</code>) e o pedido Kamino não são alterados.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button disabled={undoLoading} onClick={() => setUndoOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  color="warning"
                  variant="contained"
                  disabled={undoLoading}
                  onClick={() => {
                    handleConfirmDesfazerFaturamento().catch(() => {});
                  }}
                >
                  {undoLoading ? <Spinner size="sm" /> : 'Confirmar desfazer'}
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        }
      />
      <ModalOikosDocsViewer
        open={evidenciaOpen}
        onClose={() => setEvidenciaOpen(false)}
        tabRef="rec"
        tabId={rec?.rec_id}
        tpDocumentoId={TP_DOC_REC_LIBERACAO_FATURAMENTO}
        title="Evidência de autorização de faturamento"
        fileBaseUrl={oikosFilesBase()}
        recordFields={[
          { label: 'EP', value: rec?.rec_ep },
          { label: 'Projeto', value: rec?.recProjeto },
          { label: 'Bruto', value: currencyFormatter.format(num(rec?.rec_vlrBru)) },
          { label: 'Limite', value: fmtDateBr(rec?.rec_dtNfPrev) },
          { label: 'Liberação', value: fmtDateBr(rec?.rec_dtLibAutNf) },
        ]}
      />
    </div>
  );
};

export default function FaturamentoDashboard({
  recs,
  mesRef,
  currentView,
  setCurrentView,
  selectedRecId,
  setSelectedRecId,
  onNeedReload,
}) {
  const [dataIsLoading, setDataIsLoading] = useState(false);
  const [itemsInTable, setItemsInTable] = useState([]);
  const [title, setTitle] = useState('Faturamentos da semana');
  const [showBackButton, setShowBackButton] = useState(false);

  const [showDetails, setShowDetails] = useState(false);
  const [selectedRec, setSelectedRec] = useState(null);

  const today = useMemo(() => startOfToday(), []);
  const { start: startMonth, endExclusive: endMonthExclusive } = useMemo(() => monthRefToRange(mesRef), [mesRef]);

  const currentViewRef = useRef(currentView);
  const selectedRecIdRef = useRef(selectedRecId);
  useEffect(() => {
    currentViewRef.current = currentView;
  }, [currentView]);
  useEffect(() => {
    selectedRecIdRef.current = selectedRecId;
  }, [selectedRecId]);

  const { fatLiberados, fatBloqueadosMes, bloqHojeMes, bloqProx7Mes, fatDia, fatMes, fatMesAtraso, fatMesFaturados } = useMemo(() => {
    const withDtPrev = (recs || []).map((r) => ({ r, dtPrev: parseDate(r.rec_dtNfPrev) })).filter((x) => x.dtPrev);

    const liberados = withDtPrev.filter((x) => isLiberado(x.r) && !isFaturado(x.r)).map((x) => x.r);
    const dia = withDtPrev.filter((x) => x.dtPrev.getTime() === today.getTime()).map((x) => x.r);

    const mes = withDtPrev.filter((x) => inRange(x.dtPrev, startMonth, endMonthExclusive)).map((x) => x.r);
    const mesAtraso = withDtPrev
      .filter((x) => inRange(x.dtPrev, startMonth, endMonthExclusive) && x.dtPrev < today && !isFaturado(x.r))
      .map((x) => x.r);

    const mesFaturados = withDtPrev
      .filter((x) => inRange(x.dtPrev, startMonth, endMonthExclusive) && isFaturado(x.r))
      .map((x) => x.r);

    const onlyMonth = (x) => inRange(x.dtPrev, startMonth, endMonthExclusive);
    const bloqueadosMes = withDtPrev
      .filter((x) => onlyMonth(x) && !isLiberado(x.r) && !isFaturado(x.r))
      .map((x) => x.r);

    const bloqHoje = withDtPrev
      .filter((x) => onlyMonth(x) && !isLiberado(x.r) && !isFaturado(x.r) && x.dtPrev.getTime() === today.getTime())
      .map((x) => x.r);

    const prox7End = new Date(today);
    prox7End.setDate(today.getDate() + 7);
    const prox7EndExclusive = new Date(prox7End);
    prox7EndExclusive.setDate(prox7End.getDate() + 1);
    const bloqProx7 = withDtPrev
      .filter((x) => {
        if (!onlyMonth(x)) return false;
        if (isLiberado(x.r) || isFaturado(x.r)) return false;
        return x.dtPrev > today && x.dtPrev < prox7EndExclusive;
      })
      .map((x) => x.r);

    return {
      fatLiberados: liberados,
      fatBloqueadosMes: bloqueadosMes,
      bloqHojeMes: bloqHoje,
      bloqProx7Mes: bloqProx7,
      fatDia: dia,
      fatMes: mes,
      fatMesAtraso: mesAtraso,
      fatMesFaturados: mesFaturados,
    };
  }, [recs, today, startMonth, endMonthExclusive]);

  // mantemos fatDia para outras lógicas/uso futuro; bloqueados do card são restritos ao mês (fatBloqueadosMes)

  useEffect(() => {
    setDataIsLoading(true);
    try {
      const viewMap = {
        liberados: fatLiberados,
        bloqueados: fatBloqueadosMes,
        mesAtraso: fatMesAtraso,
        mes: fatMes,
        mes_faturados: fatMesFaturados,
      };
      const titleMap = {
        liberados: 'Faturamentos liberados',
        bloqueados: 'Faturamentos bloqueados',
        mesAtraso: 'Faturamentos do mês em atraso',
        mes: 'Faturamentos do mês',
        mes_faturados: 'Faturamentos realizados no mês',
      };
      const viewKey = currentView;
      const nextView = viewMap[viewKey] ? viewKey : 'mes';
      setItemsInTable(viewMap[nextView] || fatMes);
      setTitle(titleMap[nextView] || 'Faturamentos do mês');
      setShowBackButton(nextView !== 'mes');

      const selId = selectedRecIdRef.current;
      if (selId != null) {
        const updated = (recs || []).find((r) => Number(r?.rec_id) === Number(selId));
        if (updated) {
          setSelectedRec(updated);
          setShowDetails(true);
        }
      }
    } finally {
      setDataIsLoading(false);
    }
  }, [recs, fatLiberados, fatBloqueadosMes, fatDia, fatMes, fatMesAtraso, fatMesFaturados, currentView]);

  const columns = useMemo(() => {
    return [
      { accessorKey: 'rec_ep', header: 'EP', size: 200 },
      { accessorKey: 'recProjeto', header: 'Projeto', size: 220 },
      {
        id: 'situacao',
        header: 'Situação',
        accessorFn: (row) => getSituacao(row, today),
        Cell: ({ row }) => <SituacaoPill value={getSituacao(row.original, today)} />,
        size: 120,
      },
      {
        accessorKey: 'rec_dtNfPrev',
        header: 'Limite faturamento',
        sortingFn: 'datetime',
        accessorFn: (row) => parseDate(row.rec_dtNfPrev),
        Cell: ({ row }) => fmtDateBr(row.original.rec_dtNfPrev),
        size: 140,
      },
      {
        id: 'diasParaLimite',
        header: 'Dias p/ limite',
        accessorFn: (row) => {
          const dtPrev = parseDate(row.rec_dtNfPrev);
          if (!dtPrev) return null;
          return diffDays(dtPrev, today);
        },
        Cell: ({ row }) => {
          const dtPrev = parseDate(row.original.rec_dtNfPrev);
          if (!dtPrev) return '-';
          const d = diffDays(dtPrev, today);
          const color = d < 0 && !isFaturado(row.original) ? '#ff5757' : d === 0 ? '#4e515e' : '#6c757d';
          return <span style={{ fontWeight: 800, color }}>{d}</span>;
        },
        size: 110,
      },
      { accessorKey: 'rec_vlrBru', header: 'Valor bruto', Cell: ({ row }) => currencyFormatter.format(num(row.original.rec_vlrBru)), size: 120 },
      { accessorKey: 'rec_vlrLiq', header: 'Valor líquido', Cell: ({ row }) => currencyFormatter.format(num(row.original.rec_vlrLiq)), size: 120 },
      { accessorKey: 'rec_aut', header: 'Liberado', Cell: ({ row }) => <YesNoDot value={isLiberado(row.original)} />, size: 80 },
      { accessorKey: 'rec_fat', header: 'Faturado', Cell: ({ row }) => <YesNoDot value={isFaturado(row.original)} trueColor="#5664d2" falseColor="#ff5757" />, size: 80 },
      {
        id: 'kamino',
        header: 'Kamino',
        accessorFn: (row) => getKaminoEmitStatus(row),
        Cell: ({ row }) => <KaminoStatusPill status={getKaminoEmitStatus(row.original)} />,
        size: 140,
      },
      {
        id: 'docsSuporte',
        header: 'Docs',
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => <FatSupportDocsIcon rec={row.original} />,
        size: 64,
      },
      {
        id: 'emailTeste',
        header: 'E-mail',
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EmailTestButton rec={row.original} />
          </Box>
        ),
        size: 140,
      },
      {
        id: 'nfGrupo',
        header: 'NF',
        enableColumnFilter: false,
        accessorFn: (row) => {
          const g = recsDoMesmoGrupoNf(recs, row);
          return g.length > 1 ? 'conjunta ' + g.length : (Number(row?.nf_id) > 0 ? 'simples' : '');
        },
        Cell: ({ row }) => {
          const g = recsDoMesmoGrupoNf(recs, row.original);
          if (g.length > 1) {
            return <span style={{ fontWeight: 800, color: '#5664d2' }}>Conjunta ({g.length})</span>;
          }
          return Number(row.original?.nf_id) > 0 ? <span style={{ color: '#6c757d' }}>Simples</span> : <span style={{ color: '#9aa3ad' }}>?</span>;
        },
        size: 110,
      },
      { accessorKey: 'rec_risco', header: 'Risco', size: 80 },
      { accessorKey: 'rec_id', header: 'ID', size: 80 },
    ];
  }, [today, recs]);

  if (dataIsLoading) {
    return (
      <div className="centered">
        <Spinner />
      </div>
    );
  }

  return (
    <Container fluid>
      <h3 style={{ marginBottom: 16 }}>Faturamento</h3>

      <PacotesKaminoMes mesRef={mesRef} onNeedReload={onNeedReload} />

      <div className="d-flex justify-content-between align-items-center">
        <MetricCard
          title="Faturamentos liberados"
          recs={fatLiberados}
          className="mr-4"
          selected={currentView === 'liberados'}
          onClick={() => {
            setCurrentView('liberados');
            setItemsInTable(fatLiberados);
            setTitle('Faturamentos liberados');
            setShowBackButton(true);
          }}
        />
        <MetricCard
          title="Faturamentos bloqueados"
          recs={fatBloqueadosMes}
          className="mr-4"
          selected={currentView === 'bloqueados'}
          meta={
            <div className="d-flex flex-column" style={{ gap: 4, marginBottom: 8, fontSize: 12, color: '#6c757d' }}>
              <div className="d-flex justify-content-between" style={{ gap: 10 }}>
                <span>Hoje</span>
                <span style={{ fontWeight: 900, color: bloqHojeMes.length > 0 ? '#b46900' : '#6c757d' }}>
                  {bloqHojeMes.length}
                </span>
              </div>
              <div className="d-flex justify-content-between" style={{ gap: 10 }}>
                <span>Próximos 7 dias</span>
                <span style={{ fontWeight: 900, color: bloqProx7Mes.length > 0 ? '#b46900' : '#6c757d' }}>
                  {bloqProx7Mes.length}
                </span>
              </div>
            </div>
          }
          onClick={() => {
            setCurrentView('bloqueados');
            setItemsInTable(fatBloqueadosMes);
            setTitle('Faturamentos bloqueados');
            setShowBackButton(true);
          }}
        />
        <MetricCard
          title="Faturamentos do mês em atraso"
          recs={fatMesAtraso}
          className="mr-4"
          selected={currentView === 'mesAtraso'}
          onClick={() => {
            setCurrentView('mesAtraso');
            setItemsInTable(fatMesAtraso);
            setTitle('Faturamentos do mês em atraso');
            setShowBackButton(true);
          }}
        />
        <MetricCard
          title="Faturamentos do mês"
          recs={fatMes}
          selected={currentView === 'mes'}
          onClick={() => {
            setCurrentView('mes');
            setItemsInTable(fatMes);
            setTitle('Faturamentos do mês');
            setShowBackButton(true);
          }}
        />
      </div>

      <div className="d-flex mt-3">
        <div className="d-flex flex-column" style={{ width: '50%' }}>
          <SimpleCard
            standard={false}
            title={
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="text-truncate font-size-14 mb-2">{title}</h4>
                {showBackButton && (
                  <IconButton
                    onClick={() => {
                      setCurrentView('mes');
                      setItemsInTable(fatMes);
                      setTitle('Faturamentos do mês');
                      setShowBackButton(false);
                    }}
                  >
                    <ArrowBack />
                  </IconButton>
                )}
              </div>
            }
            text={
              <MaterialReactTable
                columns={columns}
                data={itemsInTable}
                initialState={{
                  density: 'compact',
                  pagination: { pageIndex: 0, pageSize: 10 },
                  sorting: [
                    { id: 'diasParaLimite', desc: false },
                    { id: 'rec_vlrBru', desc: true },
                  ],
                }}
                enableColumnResizing
                enableGrouping={false}
                enableGlobalFilter
                enableFilters
                enablePagination={itemsInTable.length > 10}
                muiTableBodyCellProps={{ sx: { fontSize: '10px' } }}
                muiTablePaginationProps={{
                  sx: {
                    '.MuiTablePagination-displayedRows': { fontSize: '12px' },
                    marginTop: '0px',
                    fontSize: '12px',
                  },
                  labelRowsPerPage: <span style={{ fontSize: '12px' }}>Itens por página</span>,
                  rowsPerPageOptions: [10, 50, 100],
                }}
                positionPagination="bottom"
                enableDensityToggle={false}
                enableColumnFilters
                muiTableHeadCellProps={{
                  sx: {
                    '.MuiSvgIcon-root': { fontSize: '18px' },
                    fontSize: '12px',
                    fontWeight: 'normal',
                    padding: '8px',
                  },
                }}
                muiTableBodyRowProps={({ row }) => ({
                  onClick: () => {
                    setShowDetails(true);
                    setSelectedRec(row.original);
                    setSelectedRecId(row.original?.rec_id ?? null);
                  },
                  sx: (() => {
                    const r = row.original;
                    const dtPrev = parseDate(r?.rec_dtNfPrev);
                    const dias = dtPrev ? diffDays(dtPrev, today) : null;
                    const fat = isFaturado(r);
                    const lib = isLiberado(r);
                    let bg = undefined;
                    if (!fat && dias != null && dias < 0) bg = 'rgba(255,87,87,0.12)';
                    else if (!fat && lib) bg = 'rgba(28,187,140,0.10)';
                    else if (!fat) bg = 'rgba(78,81,94,0.06)';
                    return { cursor: 'pointer', backgroundColor: bg };
                  })(),
                })}
              />
            }
          />
        </div>

        <div className="d-flex flex-column ml-2 mr-4" style={{ width: '50%' }}>
          {showDetails && (
            <DetailsCard
              rec={selectedRec}
              recs={recs}
              onRecPatched={(patch) => setSelectedRec((prev) => (prev ? { ...prev, ...patch } : prev))}
              onNeedReload={onNeedReload}
            />
          )}
        </div>
      </div>
    </Container>
  );
}

